"use client";

import { getFoodTypeLabel } from "@/lib/food-types";
import { foodCapabilityEnabled } from "@/lib/food-capabilities";

import { configurationError } from "@/lib/food-configuration";
import { cartItemPayload, compositionKey, normalizeSavedCart } from "@/lib/food-configuration";
import { quotePublicItems, type GiftPromotionQuote } from "@/lib/store";
import type { CompositionSelection } from "@/types/food-configuration";


import Image from "next/image";
import Link from "next/link";
import { FaWhatsapp, FaFacebookF, FaInstagram } from "react-icons/fa";
import { FaMoneyBillWave } from "react-icons/fa6";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Heart,
  Menu,
  MapPin,
  MessageCircle,
  Package,
  Plus,
  Search,
  Share2,
  ShoppingCart,
  Star,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/lib/api";
import { getBusinessConfig, type BusinessConfig } from "@/lib/business-config";
import {
  trackProductViewOncePerSession,
  trackStoreViewOncePerSession,
  trackWhatsappClick,
} from "@/lib/analytics";
import PublicCompanyReviews from "@/components/company/PublicCompanyReviews";
import VerifiedCompanyBadge from "@/components/company/VerifiedCompanyBadge";
import VerificationDetailsModal from "@/components/company/VerificationDetailsModal";
import FoodInstallPrompt from "@/components/pwa/FoodInstallPrompt";
import CartDrawer from "@/components/store/CartDrawer";
import RetailCart from "@/components/store/retail/RetailCart";
import { completedRetailCheckout, getRetailOrder, retailCheckoutToken, isRetailCartEdited, canResumeRetailPayment, markRetailCartEdited } from "@/lib/retail";
import AutomotiveStorefrontHome from "@/components/store/automotive/AutomotiveStorefrontHome";
import RealEstateStorefrontHome from "@/components/store/real-estate/RealEstateStorefrontHome";
import RetailStorefront from "@/components/store/retail/RetailStorefront";
import ServicesStorefront from "@/components/store/services/ServicesStorefront";
import StorefrontAdBanner from "@/components/store/StorefrontAdBanner";
import StorefrontCampaignModal from "@/components/store/StorefrontCampaignModal";
import CampaignInterestModal from "@/components/store/CampaignInterestModal";
import type { PublicStoreCampaign } from "@/types/public-campaign";
import ProductDetailsModal from "@/components/store/ProductDetailsModal";
import VehicleLeadModal, {
  type VehicleLeadMode,
} from "@/components/store/VehicleLeadModal";
import RealEstateFinancingModal from "@/components/store/RealEstateFinancingModal";
import { formatCurrency, normalizeWhatsapp } from "@/lib/order";
import { getPublicStore } from "@/lib/store";
import { getPublicItemPath } from "@/lib/public-item";
import type { CartItem, CartItemConfiguration } from "@/types/cart";
import type {
  PublicProduct,
  PublicProductVariation,
  PublicStore,
} from "@/types/store";
import type { AcceptedCardBrand } from "@/types/company";

const CART_STORAGE_PREFIX = "fornecedor_store_cart_";
const FAVORITES_STORAGE_PREFIX = "fornecedor_store_favorites_";

const CARD_PAYMENT_IMAGES: Record<
  AcceptedCardBrand,
  { src: string; alt: string; width: number; height: number }
> = {
  visa: {
    src: "/payments/visa.png",
    alt: "Visa",
    width: 294,
    height: 99,
  },
  mastercard: {
    src: "/payments/mastercard.png",
    alt: "Mastercard",
    width: 485,
    height: 378,
  },
  elo: {
    src: "/payments/elo.png",
    alt: "Elo",
    width: 3500,
    height: 1339,
  },
  amex: {
    src: "/payments/American-Express.png",
    alt: "American Express",
    width: 1270,
    height: 375,
  },
  hipercard: {
    src: "/payments/hipercard.png",
    alt: "Hipercard",
    width: 294,
    height: 131,
  },
};

function PaymentCardBrandMark({ brand }: { brand: AcceptedCardBrand }) {
  const payment = CARD_PAYMENT_IMAGES[brand];

  if (!payment) {
    return null;
  }
  return (
    <span
      title={payment.alt}
      aria-label={payment.alt}
      className="inline-flex h-9 min-w-[52px] shrink-0 items-center justify-center"
    >
      <Image
        {...payment}
        sizes="58px"
        className="max-h-7 w-auto max-w-[64px] object-contain"
      />
    </span>
  );
}

function getFavoritesStorageKey(slug: string) {
  return `${FAVORITES_STORAGE_PREFIX}${slug}`;
}

function formatPublicCnpj(value: string) {
  const digits = value.replace(/\D/g, "");

  if (digits.length !== 14) {
    return value;
  }

  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
}

function getCartStorageKey(slug: string) {
  return `${CART_STORAGE_PREFIX}${slug}`;
}

function getCartItemKey(
  productId: number,
  variationId: number | null,
  selectedOptionIds: number[] = [],
  observation = "",
  composition: CompositionSelection[] = [],
) {
  const optionKey = [...selectedOptionIds].sort((a, b) => a - b).join(",");

  const observationKey = encodeURIComponent(observation.trim().toLowerCase());

  return `${productId}:${
    variationId ?? "simple"
  }:${optionKey}:${observationKey}:${compositionKey(composition)}`;
}

function getCartItemKeyFromItem(item: CartItem) {
  return getCartItemKey(
    item.product.id,
    item.variation?.id ?? null,
    (item.selected_options ?? []).map((option) => option.option_id),
    item.observation ?? "",
    item.composition_selections ?? [],
  );
}

function getCartItemUnitPrice(item: CartItem) {
  if (item.quoted_unit_price != null) return Number(item.quoted_unit_price);
  const basePrice = Number(
    item.variation?.current_price ?? item.product.current_price,
  );

  const optionsTotal = (item.selected_options ?? []).reduce(
    (sum, option) => sum + Number(option.price_delta),
    0,
  );

  return basePrice + optionsTotal;
}

function getCartItemStockLimit(item: CartItem) {
  if (item.variation?.track_stock) {
    return item.variation.stock;
  }

  if (!item.variation && item.product.track_stock) {
    return item.product.stock;
  }

  return null;
}

function formatCep(value: string) {
  const digits = value.replace(/\D/g, "");

  if (digits.length !== 8) {
    return value;
  }

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function formatFoodAvailabilityTime(value: string | null) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(date);
}

function getSaoPauloDateKey(date: Date) {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(date);
}

function formatFoodAvailabilityLabel(
  availability: PublicStore["company"]["food_availability"],
) {
  if (!availability) {
    return "";
  }

  if (availability.status === "paused") {
    return "Pedidos pausados";
  }

  if (availability.is_open) {
    const closesAt = formatFoodAvailabilityTime(availability.closes_at);

    return closesAt ? `Aberto agora · até ${closesAt}` : "Aberto agora";
  }

  if (availability.next_opening_at) {
    const nextOpening = new Date(availability.next_opening_at);

    if (!Number.isNaN(nextOpening.getTime())) {
      const now = new Date();

      const todayKey = getSaoPauloDateKey(now);

      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const tomorrowKey = getSaoPauloDateKey(tomorrow);

      const nextKey = getSaoPauloDateKey(nextOpening);

      const time = formatFoodAvailabilityTime(availability.next_opening_at);

      if (nextKey === todayKey) {
        return `Fechado · abre hoje às ${time}`;
      }

      if (nextKey === tomorrowKey) {
        return `Fechado · abre amanhã às ${time}`;
      }

      const weekday = new Intl.DateTimeFormat("pt-BR", {
        weekday: "long",
        timeZone: "America/Sao_Paulo",
      }).format(nextOpening);

      return `Fechado · abre ${weekday} às ${time}`;
    }
  }

  return "Fechado no momento";
}

function formatAttendanceAvailabilityLabel(
  availability: PublicStore["company"]["attendance_availability"],
) {
  if (!availability) {
    return "";
  }

  if (availability.is_open) {
    const closesAt = formatFoodAvailabilityTime(availability.closes_at);

    return closesAt ? `Aberto agora · até ${closesAt}` : "Aberto agora";
  }

  if (availability.next_opening_at) {
    const nextOpening = new Date(availability.next_opening_at);

    if (!Number.isNaN(nextOpening.getTime())) {
      const now = new Date();
      const todayKey = getSaoPauloDateKey(now);

      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const tomorrowKey = getSaoPauloDateKey(tomorrow);

      const nextKey = getSaoPauloDateKey(nextOpening);

      const time = formatFoodAvailabilityTime(availability.next_opening_at);

      if (nextKey === todayKey) {
        return `Fechado · abre hoje às ${time}`;
      }

      if (nextKey === tomorrowKey) {
        return `Fechado · abre amanhã às ${time}`;
      }

      const weekday = new Intl.DateTimeFormat("pt-BR", {
        weekday: "long",
        timeZone: "America/Sao_Paulo",
      }).format(nextOpening);

      return `Fechado · abre ${weekday} às ${time}`;
    }
  }

  return "Fechado no momento";
}

function buildGeneralWhatsappUrl(whatsapp: string, companyName: string) {
  const number = normalizeWhatsapp(whatsapp);

  const message =
    `Olá! Encontrei a empresa ${companyName} ` +
    "no fornecedor.net e gostaria de mais informações.";

  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

type StoreWhatsappContactProps = {
  company: PublicStore["company"];
  variant: "header" | "hero";
  ariaLabel?: string;
};

function StoreWhatsappContact({
  company,
  variant,
  ariaLabel,
}: StoreWhatsappContactProps) {
  const contacts =
    company.whatsapps && company.whatsapps.length > 0
      ? company.whatsapps
      : company.whatsapp
        ? [
            {
              label: "Principal",
              number: company.whatsapp,
              is_primary: true,
            },
          ]
        : [];

  if (contacts.length === 0) {
    return null;
  }

  const buttonClass =
    variant === "header"
      ? "inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#25D366] px-3 text-sm font-black text-white transition hover:bg-[#1EBE5D] sm:px-4"
      : "inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-7 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#1EBE5D]";

  const iconClass =
    variant === "header" ? "h-5 w-5 shrink-0" : "h-6 w-6 shrink-0";

  const buttonLabel =
    variant === "header" ? (
      <span className="hidden xl:inline">WhatsApp</span>
    ) : (
      <>Falar no WhatsApp</>
    );

  if (contacts.length === 1) {
    const contact = contacts[0];

    return (
      <a
        aria-label={ariaLabel}
        href={buildGeneralWhatsappUrl(contact.number, company.name)}
        onClick={() => trackWhatsappClick(company.slug, "store_header")}
        target="_blank"
        rel="noreferrer"
        className={buttonClass}
      >
        <FaWhatsapp className={iconClass} aria-hidden="true" />
        {buttonLabel}
      </a>
    );
  }

  return (
    <div className="group relative shrink-0">
      <button
        type="button"
        className={buttonClass}
        aria-label="Escolher WhatsApp"
      >
        <FaWhatsapp className={iconClass} aria-hidden="true" />

        {buttonLabel}

        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
      </button>

      <div
        className={
          "invisible absolute right-0 top-full z-50 mt-2 " +
          "w-72 translate-y-1 rounded-2xl border border-slate-200 " +
          "bg-white p-2 opacity-0 shadow-xl transition " +
          "group-focus-within:visible group-focus-within:translate-y-0 " +
          "group-focus-within:opacity-100"
        }
      >
        <div className="px-3 pb-2 pt-1">
          <p className="text-xs font-black uppercase tracking-wide text-slate-400">
            Falar com a empresa
          </p>
        </div>

        <div className="space-y-1">
          {contacts.map((contact, index) => (
            <a
              key={contact.number + "-" + index}
              href={buildGeneralWhatsappUrl(contact.number, company.name)}
              onClick={() => trackWhatsappClick(company.slug, "store_header")}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 rounded-xl px-3 py-3 transition hover:bg-emerald-50"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <FaWhatsapp className="h-5 w-5" aria-hidden="true" />
              </span>

              <span className="min-w-0">
                <span className="block truncate text-sm font-black text-slate-800">
                  {contact.label ||
                    (contact.is_primary ? "Principal" : "WhatsApp")}
                </span>

                <span className="block text-xs font-semibold text-slate-500">
                  {contact.number}
                </span>
              </span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

type PublicStorePageProps = {
  slug: string;
  initialStore: PublicStore | null;
  initialErrorMessage?: string;
  ratingAverage?: number | null;
  ratingCount?: number;
  instagramUrl?: string;
  facebookUrl?: string;
  primaryColor?: string;
};

export default function PublicStorePage({
  slug,
  initialStore,
  initialErrorMessage = "",
  ratingAverage = null,
  ratingCount = 0,
  instagramUrl = "",
  facebookUrl = "",
  primaryColor = "",
}: PublicStorePageProps) {
  const [store, setStore] = useState<PublicStore | null>(initialStore);
  const [campaignInterest, setCampaignInterest] =
    useState<PublicStoreCampaign | null>(null);

  const businessConfig = getBusinessConfig(store?.company.business_type);
  const showCart = Boolean(
    store &&
    ((store.company.business_type === "food" && foodCapabilityEnabled(store.company, "food_online_orders")) ||
      (store.company.commerce_enabled &&
        (store.company.business_type !== "services" ||
          store.products.some((item) => item.item_kind === "product")))),
  );

  const [isLoading, setIsLoading] = useState(
    !initialStore && !initialErrorMessage,
  );

  const [errorMessage, setErrorMessage] = useState(initialErrorMessage);

  const [search, setSearch] = useState("");
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);

  const [isVerificationDetailsOpen, setIsVerificationDetailsOpen] =
    useState(false);

  const [categoryId, setCategoryId] = useState<number | null>(null);

  const [catalogView, setCatalogView] = useState<
    "home" | "all" | "featured" | "favorites" | "promotions"
  >(
    initialStore?.company.business_type === "food" ||
    initialStore?.company.remove_brand
      ? "home"
      : "all",
  );

  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);

  useEffect(() => {
    if (!initialStore && store?.company.remove_brand) {
      setCatalogView("home");
    }
  }, [initialStore, store?.company.remove_brand]);

  const [isCatalogMenuOpen, setIsCatalogMenuOpen] = useState(false);

  const [mobileExpandedCategoryId, setMobileExpandedCategoryId] = useState<
    number | null
  >(null);

  const [desktopCategoryNavHasOverflow, setDesktopCategoryNavHasOverflow] =
    useState(false);


  useEffect(() => {
    const nav = document.getElementById(
      "catalog-category-navbar",
    );

    if (!nav) {
      setDesktopCategoryNavHasOverflow(false);
      return;
    }

    const updateDesktopCategoryNavOverflow = () => {
      const hasOverflow =
        nav.scrollWidth > nav.clientWidth + 1;

      setDesktopCategoryNavHasOverflow(hasOverflow);

      if (!hasOverflow && nav.scrollLeft !== 0) {
        nav.scrollLeft = 0;
      }
    };

    updateDesktopCategoryNavOverflow();

    const resizeObserver = new ResizeObserver(
      updateDesktopCategoryNavOverflow,
    );

    resizeObserver.observe(nav);

    for (const child of Array.from(nav.children)) {
      resizeObserver.observe(child);
    }

    window.addEventListener(
      "resize",
      updateDesktopCategoryNavOverflow,
    );

    return () => {
      resizeObserver.disconnect();

      window.removeEventListener(
        "resize",
        updateDesktopCategoryNavOverflow,
      );
    };
  }, [store]);

  const [desktopCategorySubmenu, setDesktopCategorySubmenu] = useState<{
    categoryId: number;
    left: number;
    top: number;
  } | null>(null);

  const desktopCategoryCloseTimeoutRef =
    useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleDesktopCategoryOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target) {
        return;
      }

      const navbar = document.getElementById(
        "catalog-category-navbar",
      );

      const submenu = document.querySelector(
        '[data-desktop-category-submenu="true"]',
      );

      if (
        navbar?.contains(target) ||
        submenu?.contains(target)
      ) {
        return;
      }

      setDesktopCategorySubmenu(null);
    };

    document.addEventListener(
      "mousedown",
      handleDesktopCategoryOutsideClick,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleDesktopCategoryOutsideClick,
      );
    };
  }, []);


  const [cart, setCart] = useState<CartItem[]>([]);
  const [retailRecovery, setRetailRecovery] = useState<{ count: number; total: string; label: string } | null>(null);

  const isCartInitializedRef = useRef(false);

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    setRetailRecovery(null);
    if (store?.company.business_type !== "retail" || isRetailCartEdited(slug)) return;
    const token = retailCheckoutToken(completedRetailCheckout(slug));
    if (!token) return;
    const controller = new AbortController();
    getRetailOrder(token, controller.signal).then(order => {
      if (!controller.signal.aborted && order.company.slug === slug && (canResumeRetailPayment(order)
          || ["creating", "uncertain"].includes(order.online_payment?.status ?? "") || order.online_payment?.expiry_pending_confirmation))
        setRetailRecovery({ count: order.items.reduce((sum, item) => sum + item.quantity, 0), total: order.total,
          label: canResumeRetailPayment(order) ? "Continuar pagamento" : "Acompanhar pagamento" });
    }).catch(() => {});
    return () => controller.abort();
  }, [slug, store?.company.business_type, isCartOpen, cart.length]);

  const [selectedProduct, setSelectedProduct] = useState<PublicProduct | null>(
    null,
  );

  const [favoriteProductIds, setFavoriteProductIds] = useState<number[]>([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(getFavoritesStorageKey(slug));
      const parsed = stored ? JSON.parse(stored) : [];

      setFavoriteProductIds(
        Array.isArray(parsed)
          ? parsed.filter((id): id is number => Number.isInteger(id))
          : [],
      );
    } catch {
      setFavoriteProductIds([]);
    } finally {
      setFavoritesLoaded(true);
    }
  }, [slug]);

  useEffect(() => {
    if (!favoritesLoaded) {
      return;
    }

    window.localStorage.setItem(
      getFavoritesStorageKey(slug),
      JSON.stringify(favoriteProductIds),
    );
  }, [favoriteProductIds, favoritesLoaded, slug]);

  function toggleFavoriteProduct(productId: number) {
    setFavoriteProductIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId],
    );
  }

  const [vehicleLeadMode, setVehicleLeadMode] =
    useState<VehicleLeadMode | null>(null);
  const [isRealEstateFinancingOpen, setIsRealEstateFinancingOpen] =
    useState(false);
  const [realEstateFinancingProductId, setRealEstateFinancingProductId] =
    useState<number | null>(null);

  useEffect(() => {
    if (initialStore || initialErrorMessage) {
      return;
    }

    let isCancelled = false;

    setIsLoading(true);
    setErrorMessage("");

    getPublicStore(slug)
      .then((response) => {
        if (!isCancelled) {
          setStore(response);
        }
      })
      .catch((error: unknown) => {
        if (isCancelled) {
          return;
        }

        if (error instanceof ApiError && error.status === 404) {
          setErrorMessage("Esta empresa não foi encontrada.");
          return;
        }

        setErrorMessage("Não foi possível carregar esta página.");
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [slug, initialStore, initialErrorMessage]);

  useEffect(() => {
    if (!store) {
      return;
    }

    trackStoreViewOncePerSession(store.company.slug);
  }, [store]);

  useEffect(() => {
    if (!store) {
      return;
    }

    if (
      store.company.business_type === "food" ||
      store.company.remove_brand
    ) {
      setCatalogView("home");
    } else {
      const hasFeaturedProducts = store.products.some(
        (product) => product.is_featured,
      );

      setCatalogView(hasFeaturedProducts ? "featured" : "all");
    }

    setCategoryId(null);
    setSubcategoryId(null);
  }, [store]);

  useEffect(() => {
    let isCancelled = false;

    isCartInitializedRef.current = false;

    Promise.resolve().then(() => {
      if (isCancelled || typeof window === "undefined") {
        return;
      }

      try {
        const savedCart = localStorage.getItem(getCartStorageKey(slug));

        if (savedCart) {
          const parsedCart = JSON.parse(savedCart) as CartItem[];

          if (Array.isArray(parsedCart)) {
            setCart(normalizeSavedCart(parsedCart));
          }
        }
      } catch {
        localStorage.removeItem(getCartStorageKey(slug));

        setCart([]);
      } finally {
        isCartInitializedRef.current = true;
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (typeof window === "undefined" || !isCartInitializedRef.current) {
      return;
    }

    localStorage.setItem(getCartStorageKey(slug), JSON.stringify(cart));
  }, [cart, slug]);

  const cartQuoteKey = JSON.stringify(cart.map(cartItemPayload));
  const [cartQuoteState, setCartQuoteState] = useState<{key: string; error?: string} | null>(null);
  const [giftPromotions, setGiftPromotions] = useState<GiftPromotionQuote[]>([]);
  const needsQuote = store?.company.business_type === "food" && showCart;
  useEffect(() => {
    if (!needsQuote || cart.length === 0) {
      setGiftPromotions([]);
      return;
    }
    let cancelled = false;
    quotePublicItems(slug, JSON.parse(cartQuoteKey)).then(response => {
      if (cancelled) return;
      setCart(current => current.map((item, index) => ({...item,
        quoted_unit_price: response.items[index].unit_price,
        selected_options: response.items[index].option_selections,
        configuration_snapshot: response.items[index].configuration_snapshot,
      })));
      setGiftPromotions(response.gift_promotions ?? []);
      setCartQuoteState({key: cartQuoteKey});
    }).catch(error => {
      if (!cancelled) {
        setGiftPromotions([]);
        setCartQuoteState({
          key: cartQuoteKey,
          error: configurationError(error),
        });
      }
    });
    return () => {cancelled = true;};
  }, [cartQuoteKey, needsQuote, slug, cart.length]);
  async function revalidateCart() {
    if (!needsQuote) return true;
    const response = await quotePublicItems(slug, cart.map(cartItemPayload));
    setGiftPromotions(response.gift_promotions ?? []);
    const unchanged = response.items.every((quote, index) => quote.unit_price === cart[index].quoted_unit_price);
    setCart(current => current.map((item,index) => ({...item, quoted_unit_price: response.items[index].unit_price, selected_options: response.items[index].option_selections, configuration_snapshot: response.items[index].configuration_snapshot})));
    return unchanged;
  }

  const products = useMemo(() => {
    if (!store) {
      return [];
    }

    const query = search.trim().toLowerCase();

    return store.products
      .filter((product) => {
        const hasFeaturedProducts = store.products.some(
          (item) => item.is_featured,
        );

        const matchesCatalogView =
          catalogView === "home"
            ? !hasFeaturedProducts || product.is_featured
            : catalogView === "featured"
              ? product.is_featured
              : catalogView === "favorites"
                ? favoriteProductIds.includes(product.id)
                : catalogView === "promotions"
                  ? store.promoted_products.some(
                      (item) => item.id === product.id,
                    )
                  : true;

        const matchesCategory =
          categoryId === null || product.category === categoryId;

        const matchesSubcategory =
          subcategoryId === null || product.subcategory === subcategoryId;

        const matchesSearch =
          !query ||
          product.name.toLowerCase().includes(query) ||
          product.short_description.toLowerCase().includes(query) ||
          product.category_name?.toLowerCase().includes(query) ||
          product.subcategory_name?.toLowerCase().includes(query);

        return (
          matchesCatalogView &&
          matchesCategory &&
          matchesSubcategory &&
          matchesSearch
        );
      })
      .sort((a, b) =>
        store.company.business_type === "services" &&
        store.company.commerce_enabled
          ? Number(a.item_kind === "product") -
            Number(b.item_kind === "product")
          : 0,
      );
  }, [
    store,
    search,
    categoryId,
    subcategoryId,
    catalogView,
    favoriteProductIds,
  ]);

  const cartItemsCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart],
  );

  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) => sum + getCartItemUnitPrice(item) * item.quantity,
        0,
      ),
    [cart],
  );

  function openProductDetails(
    product: PublicProduct,
  ) {
    let productToOpen = product;

    if (
      catalogView === "promotions" &&
      !product.promotion_offer
    ) {
      productToOpen =
        store?.promoted_products.find(
          (promotedProduct) =>
            promotedProduct.id === product.id,
        ) ?? product;
    }

    setSelectedProduct(
      productToOpen,
    );
  }

  function addCartItem(
    product: PublicProduct,
    variation: PublicProductVariation | null,
    configuration: CartItemConfiguration = {
      selected_options: [],
      observation: "",
      quantity: 1,
    },
  ) {
    if (!showCart || product.item_kind !== "product" || !product.is_available || product.food_category_available === false) {
      return;
    }

    if (variation && !variation.is_available) {
      return;
    }

    const requestedQuantity = Math.max(1, Math.trunc(configuration.quantity));

    const selectedOptions = configuration.selected_options ?? [];

    const observation = configuration.observation?.trim() ?? "";

    const itemKey = getCartItemKey(
      product.id,
      variation?.id ?? null,
      selectedOptions.map((option) => option.option_id),
      observation,
      configuration.composition_selections ?? [],
    );

    // Only an explicit Add action marks Retail editing; restoration/setCart do not.
    if (company.business_type === "retail" && completedRetailCheckout(company.slug)) {
      const existing = cart.find(item => getCartItemKeyFromItem(item) === itemKey);
      const limit = getCartItemStockLimit(existing ?? { product, variation, quantity: requestedQuantity, selected_options: selectedOptions, observation });
      if (limit === null || (existing?.quantity ?? 0) + requestedQuantity <= limit) markRetailCartEdited(company.slug);
    }

    setCart((current) => {
      const existingItem = current.find(
        (item) => getCartItemKeyFromItem(item) === itemKey,
      );

      if (existingItem) {
        const stockLimit = getCartItemStockLimit(existingItem);

        const nextQuantity = existingItem.quantity + requestedQuantity;

        if (stockLimit !== null && nextQuantity > stockLimit) {
          return current;
        }

        return current.map((item) =>
          getCartItemKeyFromItem(item) === itemKey
            ? {
                ...item,
                quantity: nextQuantity,
              }
            : item,
        );
      }

      const newItem: CartItem = {
        product,
        variation,
        quantity: requestedQuantity,
        selected_options: selectedOptions,
        composition_selections: configuration.composition_selections ?? [],
        quoted_unit_price: configuration.quoted_unit_price,
        configuration_snapshot: configuration.configuration_snapshot,
        observation,
      };

      const stockLimit = getCartItemStockLimit(newItem);

      if (stockLimit !== null && requestedQuantity > stockLimit) {
        return current;
      }

      return [...current, newItem];
    });

    if (company.business_type !== "food") {
      setIsCartOpen(true);
    }
  }

  function addToCart(product: PublicProduct) {
    if (!showCart || product.item_kind !== "product" || !product.is_available || product.food_category_available === false) {
      return;
    }

    if (product.has_variations || (product.option_groups?.length ?? 0) > 0) {
      openProductDetails(product);
      return;
    }

    addCartItem(product, null);
  }

  function updateQuantity(itemKey: string, quantity: number) {
    if (quantity <= 0) {
      removeFromCart(itemKey);
      return;
    }

    setCart((current) =>
      current.map((item) => {
        const currentItemKey = getCartItemKeyFromItem(item);

        if (currentItemKey !== itemKey) {
          return item;
        }

        const stockLimit = getCartItemStockLimit(item);

        const safeQuantity =
          stockLimit === null ? quantity : Math.min(quantity, stockLimit);

        return {
          ...item,
          quantity: safeQuantity,
        };
      }),
    );
  }

  function removeFromCart(itemKey: string) {
    setCart((current) =>
      current.filter((item) => getCartItemKeyFromItem(item) !== itemKey),
    );
  }

  function clearCart() {
    setCart([]);
  }

  if (isLoading) {
    return (
      <main className="min-h-screen animate-pulse bg-slate-50">
        <header className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex min-h-20 max-w-[1380px] items-center justify-between px-5 lg:px-8">
            <div className="h-7 w-40 rounded-lg bg-slate-200" />
            <div className="h-11 w-32 rounded-2xl bg-slate-200" />
          </div>
        </header>

        <section className="bg-slate-300">
          <div className="mx-auto flex min-h-[440px] max-w-[1380px] items-end gap-7 px-5 pb-16 lg:px-8 lg:pb-20">
            <div className="h-36 w-36 shrink-0 rounded-[2rem] bg-slate-200 sm:h-40 sm:w-40" />

            <div className="flex-1">
              <div className="h-6 w-48 rounded-full bg-slate-200" />
              <div className="mt-5 h-12 max-w-xl rounded-xl bg-slate-200" />
              <div className="mt-4 h-5 w-56 rounded-lg bg-slate-200" />
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-[1380px] px-5 py-8 lg:px-8 lg:py-12">
          <div className="h-8 w-72 rounded-xl bg-slate-200" />
          <div className="mt-3 h-5 w-96 max-w-full rounded-lg bg-slate-200" />

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-3xl border border-slate-200 bg-white"
              >
                <div className="aspect-[4/3] bg-slate-200" />

                <div className="p-5">
                  <div className="h-3 w-24 rounded bg-slate-200" />
                  <div className="mt-4 h-6 w-4/5 rounded bg-slate-200" />
                  <div className="mt-3 h-4 w-full rounded bg-slate-200" />
                  <div className="mt-2 h-4 w-2/3 rounded bg-slate-200" />
                  <div className="mt-6 h-7 w-32 rounded bg-slate-200" />
                  <div className="mt-5 h-11 w-full rounded-2xl bg-slate-200" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (!store || errorMessage) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-5">
        <div className="max-w-md text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-200 text-slate-500">
            <Building2 size={30} />
          </div>

          <h1 className="mt-5 text-2xl font-bold text-slate-950">
            Empresa não encontrada
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            {errorMessage}
          </p>

          <Link
            href="/fornecedores"
            className="mt-6 inline-flex h-11 items-center gap-2 rounded-2xl bg-blue-600 px-5 text-sm font-bold text-white"
          >
            <ArrowLeft size={18} />
            Encontrar fornecedores
          </Link>
        </div>
      </main>
    );
  }

  const { company, categories } = store;
  const isAutomotiveHome = Boolean(company.business_type === "vehicles");
  const isRealEstateHome = company.business_type === "real_estate";
  const isRetailHome = company.business_type === "retail";
  const isServicesHome = company.business_type === "services";

  const itemConfig = (product: PublicProduct) =>
    company.business_type === "services" && product.item_kind === "product"
      ? getBusinessConfig("retail")
      : businessConfig;

  const campaignLeadAllowed = [
    "real_estate",
    "vehicles",
    "services",
    "consortium_insurance",
    "other",
  ].includes(company.business_type);

  const campaignBanners = store.campaigns?.banners;

  function openCampaignProducts(ids: number[]) {
    const product = store?.products.find(
      (item) => ids.includes(item.id) && item.is_available,
    );

    if (product) {
      openProductDetails(product);
      trackProductViewOncePerSession(company.slug, product.id);
    }
  }

  const selectedCategory =
    categories.find((category) => category.id === categoryId) ?? null;

  const promotionCatalogProducts =
    catalogView === "promotions"
      ? store.promoted_products.filter((promotionProduct) =>
          products.some(
            (catalogProduct) =>
              catalogProduct.id === promotionProduct.id,
          ),
        )
      : products;


  const selectedProductForModal =
    selectedProduct &&
    catalogView === "promotions" &&
    !selectedProduct.promotion_offer
      ? store.promoted_products.find(
          (promotedProduct) =>
            promotedProduct.id ===
            selectedProduct.id,
        ) ?? selectedProduct
      : selectedProduct;


  return (
    <main className="min-h-screen bg-slate-50">
      {company.business_type === "food" && (
        <FoodInstallPrompt company={company} />
      )}
      {(isAutomotiveHome || isRealEstateHome || isServicesHome) && (
        <StorefrontCampaignModal
          campaign={store.campaigns?.modal}
          companyId={company.id}
          onProducts={openCampaignProducts}
          onLead={
            campaignLeadAllowed
              ? () => {
                  const campaign = store.campaigns?.modal;
                  if (campaign) setCampaignInterest(campaign);
                }
              : undefined
          }
        />
      )}

      {campaignLeadAllowed && campaignInterest && (
        <CampaignInterestModal
          campaign={campaignInterest}
          companySlug={company.slug}
          onClose={() => setCampaignInterest(null)}
        />
      )}

      <div data-vehicle-modal="public-store">
        {company.business_type === "vehicles" && vehicleLeadMode && (
          <VehicleLeadModal
            companySlug={company.slug}
            mode={vehicleLeadMode}
            products={store.products}
            onClose={() => setVehicleLeadMode(null)}
          />
        )}
      </div>

      {company.business_type === "real_estate" && isRealEstateFinancingOpen && (
        <RealEstateFinancingModal
          companySlug={company.slug}
          products={store.products}
          initialProductId={realEstateFinancingProductId}
          onClose={() => {
            setIsRealEstateFinancingOpen(false);
            setRealEstateFinancingProductId(null);
          }}
        />
      )}
      {isServicesHome ? (
        <ServicesStorefront
          store={store}
          primaryColor={primaryColor}
          instagramUrl={instagramUrl}
          facebookUrl={facebookUrl}
          formattedCnpj={formatPublicCnpj(company.cnpj)}
          attendanceLabel={formatAttendanceAvailabilityLabel(
            company.attendance_availability,
          )}
          contact={
            <StoreWhatsappContact
              company={company}
              variant="hero"
              ariaLabel="Falar com a empresa no WhatsApp"
            />
          }
          reviews={
            <PublicCompanyReviews
              slug={company.slug}
              initialRatingAverage={ratingAverage}
              initialRatingCount={ratingCount}
            />
          }
          cart={cart}
          cartItemsCount={cartItemsCount}
          showCart={showCart}
          onOpenProduct={(product) => {
            trackProductViewOncePerSession(company.slug, product.id);
            openProductDetails(product);
          }}
          onAdd={addToCart}
          onOpenCart={() => setIsCartOpen(true)}
          onCampaignProducts={openCampaignProducts}
          onCampaignLead={setCampaignInterest}
          onVerification={() => setIsVerificationDetailsOpen(true)}
        />
      ) : isRetailHome ? (
        <RetailStorefront
          store={store}
          primaryColor={primaryColor}
          instagramUrl={instagramUrl}
          facebookUrl={facebookUrl}
          formattedCnpj={formatPublicCnpj(company.cnpj)}
          attendanceLabel={formatAttendanceAvailabilityLabel(
            company.attendance_availability,
          )}
          contact={
            <StoreWhatsappContact
              company={company}
              variant="header"
              ariaLabel="Falar com a empresa no WhatsApp"
            />
          }
          cart={cart}
          cartItemsCount={cartItemsCount}
          showCart={showCart}
          favoriteProductIds={favoriteProductIds}
          onToggleFavorite={toggleFavoriteProduct}
          onOpenProduct={(product) => {
            trackProductViewOncePerSession(company.slug, product.id);
            openProductDetails(product);
          }}
          onAdd={addToCart}
          onOpenCart={() => setIsCartOpen(true)}
          onCampaignProducts={openCampaignProducts}
        />
      ) : isAutomotiveHome ? (
        <AutomotiveStorefrontHome
          store={store}
          primaryColor={primaryColor}
          instagramUrl={instagramUrl}
          facebookUrl={facebookUrl}
          formattedCnpj={formatPublicCnpj(company.cnpj)}
          attendanceLabel={formatAttendanceAvailabilityLabel(
            company.attendance_availability,
          )}
          contact={<StoreWhatsappContact company={company} variant="hero" />}
          headerContact={
            <StoreWhatsappContact
              company={company}
              variant="header"
              ariaLabel="Falar com a loja no WhatsApp"
            />
          }
          favoriteProductIds={favoriteProductIds}
          onToggleFavorite={toggleFavoriteProduct}
          onOpenProduct={(product) => {
            trackProductViewOncePerSession(company.slug, product.id);

            const slugPrefix =
              window.location.pathname === `/${company.slug}` ||
              window.location.pathname.startsWith(`/${company.slug}/`)
                ? `/${company.slug}`
                : "";

            const href = `${slugPrefix}/veiculo/${product.slug}`;

            const isDesktop = window.matchMedia("(min-width: 768px)").matches;

            if (isDesktop) {
              window.open(href, "_blank", "noopener,noreferrer");
              return;
            }

            window.location.assign(href);
          }}
          onCampaignProducts={openCampaignProducts}
          onCampaignLead={setCampaignInterest}
          onVehicleLead={setVehicleLeadMode}
          onVerification={() => setIsVerificationDetailsOpen(true)}
          reviews={
            <PublicCompanyReviews
              slug={company.slug}
              initialRatingAverage={ratingAverage}
              initialRatingCount={ratingCount}
            />
          }
        />
      ) : isRealEstateHome ? (
        <RealEstateStorefrontHome
          store={store}
          primaryColor={primaryColor}
          instagramUrl={instagramUrl}
          facebookUrl={facebookUrl}
          formattedCnpj={formatPublicCnpj(company.cnpj)}
          attendanceLabel={formatAttendanceAvailabilityLabel(
            company.attendance_availability,
          )}
          contact={<StoreWhatsappContact company={company} variant="hero" />}
          headerContact={
            <StoreWhatsappContact
              company={company}
              variant="header"
              ariaLabel="Falar com a imobiliária no WhatsApp"
            />
          }
          favoriteProductIds={favoriteProductIds}
          onToggleFavorite={toggleFavoriteProduct}
          onOpenProduct={(product) => {
            trackProductViewOncePerSession(company.slug, product.id);

            const slugPrefix =
              window.location.pathname === `/${company.slug}` ||
              window.location.pathname.startsWith(`/${company.slug}/`)
                ? `/${company.slug}`
                : "";

            const href = `${slugPrefix}/imovel/${product.slug}`;

            const isDesktop = window.matchMedia("(min-width: 768px)").matches;

            if (isDesktop) {
              window.open(href, "_blank", "noopener,noreferrer");
              return;
            }

            window.location.assign(href);
          }}
          onCampaignProducts={openCampaignProducts}
          onCampaignLead={setCampaignInterest}
          onFinancing={() => setIsRealEstateFinancingOpen(true)}
          onVerification={() => setIsVerificationDetailsOpen(true)}
          reviews={
            <PublicCompanyReviews
              slug={company.slug}
              initialRatingAverage={ratingAverage}
              initialRatingCount={ratingCount}
            />
          }
        />
      ) : (
        <>
          {company.business_type !== "food" && (
            <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
              <div className="bg-white">
                  <div className="mx-auto flex min-h-20 max-w-[1380px] items-center justify-between gap-5 px-5 lg:px-8">
                    <a
                      href="#inicio"
                      className="flex min-w-0 items-center gap-3"
                    >
                      {company.logo_url ? (
                        <img
                          src={company.logo_url}
                          alt={company.name}
                          className="h-14 w-[72px] shrink-0 object-contain"
                        />
                      ) : (
                        <div
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-black text-white"
                          style={{
                            backgroundColor: "#2563EB",
                          }}
                        >
                          {company.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}

                      <div className="min-w-0">
                        <span className="block truncate text-base font-black text-slate-900 sm:text-lg">
                          {company.name}
                        </span>

                        {company.business_type === "real_estate" &&
                          company.creci_number &&
                          company.creci_state && (
                            <span className="mt-0.5 block truncate text-xs font-semibold text-slate-500">
                              CRECI-{company.creci_state} {company.creci_number}
                            </span>
                          )}
                      </div>
                    </a>

                    <div className="flex items-center justify-end gap-2 sm:gap-3">
                      {favoriteProductIds.length > 0 && (
                        <button
                          type="button"
                          aria-label="Abrir favoritos"
                          title="Favoritos"
                          onClick={() => {
                            setCatalogView("favorites");
                            setCategoryId(null);
                            setSubcategoryId(null);

                            document
                              .getElementById("catalogo")
                              ?.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="inline-flex shrink-0 items-center justify-center bg-transparent p-2 text-slate-600 transition hover:scale-110 hover:text-rose-500"
                        >
                          <Heart size={25} className="fill-none" />
                        </button>
                      )}

                      {showCart && (
                        <button
                          type="button"
                          onClick={() => setIsCartOpen(true)}
                          className="relative inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 sm:px-4"
                        >
                          <ShoppingCart size={18} />

                          <span className="hidden xl:inline">Pedidos</span>

                          {cartItemsCount > 0 && (
                            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[10px] font-black text-white">
                              {cartItemsCount}
                            </span>
                          )}
                        </button>
                      )}

                      {(company.whatsapps?.length || company.whatsapp) && (
                        <StoreWhatsappContact
                          company={company}
                          variant="header"
                        />
                      )}
                    </div>
                  </div>

                  <div className="border-t border-slate-100">
                    <nav className="mx-auto hidden min-h-12 max-w-[1380px] flex-wrap items-center justify-center gap-1 px-5 lg:flex lg:px-8">
                      <a
                        href="#inicio"
                        onClick={() => {
                          setCatalogView("home");
                          setCategoryId(null);
                          setSubcategoryId(null);
                        }}
                        className={`rounded-xl px-3 py-2 text-sm font-bold transition ${
                          catalogView === "home"
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-600 hover:bg-slate-50 hover:text-blue-600"
                        }`}
                      >
                        Início
                      </a>

                      {company.description && (
                        <a
                          href="#sobre"
                          className="rounded-xl px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 hover:text-blue-600"
                        >
                          Empresa
                        </a>
                      )}

                      {categories.map((category) => {
                        const hasSubcategories =
                          category.subcategories.length > 0;

                        return (
                          <div key={category.id} className="group relative">
                            <button
                              type="button"
                              onClick={() => {
                                setCatalogView("all");
                                setCategoryId(category.id);
                                setSubcategoryId(null);

                                document
                                  .getElementById("catalogo")
                                  ?.scrollIntoView({
                                    behavior: "smooth",
                                  });
                              }}
                              className={`flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-bold transition ${
                                categoryId === category.id
                                  ? "bg-blue-50 text-blue-700"
                                  : "text-slate-600 hover:bg-slate-50 hover:text-blue-600"
                              }`}
                            >
                              <span>{category.name}</span>

                              {hasSubcategories && (
                                <ChevronDown
                                  size={14}
                                  className="transition-transform group-hover:rotate-180"
                                />
                              )}
                            </button>

                            {hasSubcategories && (
                              <div className="invisible absolute left-0 top-full z-50 min-w-52 pt-2 opacity-0 transition group-hover:visible group-hover:opacity-100">
                                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-xl">
                                  {category.subcategories.map((subcategory) => (
                                    <button
                                      type="button"
                                      key={subcategory.id}
                                      onClick={() => {
                                        setCatalogView("all");
                                        setCategoryId(category.id);
                                        setSubcategoryId(subcategory.id);

                                        document
                                          .getElementById("catalogo")
                                          ?.scrollIntoView({
                                            behavior: "smooth",
                                          });
                                      }}
                                      className="block w-full px-4 py-2.5 text-left text-sm font-semibold text-slate-600 transition hover:bg-blue-50 hover:text-blue-700"
                                    >
                                      {subcategory.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {company.business_type === "vehicles" && (
                        <>
                          <button
                            type="button"
                            data-vehicle-native="financing-premium"
                            onClick={() => setVehicleLeadMode("financing")}
                            className="rounded-xl px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 hover:text-blue-600"
                          >
                            Financiamento
                          </button>

                          <button
                            type="button"
                            data-vehicle-native="appraisal-premium"
                            onClick={() =>
                              setVehicleLeadMode("vehicle_appraisal")
                            }
                            className="rounded-xl px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 hover:text-blue-600"
                          >
                            Avalie seu carro
                          </button>
                        </>
                      )}

                      {company.business_type === "real_estate" && (
                        <button
                          type="button"
                          data-real-estate-financing="premium"
                          onClick={() => {
                            setRealEstateFinancingProductId(null);
                            setIsRealEstateFinancingOpen(true);
                          }}
                          className="rounded-xl px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 hover:text-blue-600"
                        >
                          Financiamento
                        </button>
                      )}
                    </nav>
                  </div>
                </div>
            </header>
          )}

          {company.business_type === "food" &&
            showCart && !isCartOpen && (
              <button
                type="button"
                onClick={() => setIsCartOpen(true)}
                aria-label="Abrir pedidos"
                className="fixed right-4 top-4 z-[70] inline-flex h-11 items-center gap-2 rounded-2xl bg-blue-600 px-4 text-sm font-bold text-white shadow-lg transition hover:bg-blue-700 sm:right-6 sm:top-6"
              >
                <ShoppingCart size={18} />

                <span className="hidden sm:inline">Pedidos</span>

                {cartItemsCount > 0 && (
                  <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-1.5 text-xs font-black text-blue-600">
                    {cartItemsCount}
                  </span>
                )}
              </button>
            )}

          {/* Banner da empresa */}
          <section id="inicio" className="scroll-mt-20 bg-white">
            <div className="relative aspect-[1920/570] w-full overflow-hidden bg-slate-200">
              {company.banner_url ? (
                <img
                  src={company.banner_url}
                  alt={`Banner da empresa ${company.name}`}
                  className="block h-full w-full object-cover"
                />
              ) : (
                <div className="h-full bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700" />
              )}
            </div>

            {/* Identidade da empresa */}
            {(company.business_type === "food" || !company.remove_brand) && (
              <div className="border-b border-slate-200 bg-white">
                <div className="relative mx-auto max-w-[1380px] px-5 pb-7 lg:px-8 lg:pb-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:gap-8">
                    {/* Logo parcialmente sobre o banner */}
                    <div className="-mt-12 shrink-0 sm:-mt-14 lg:-mt-16">
                      <div
                        className="flex h-32 w-32 items-center justify-center rounded-[1.75rem] border-4 border-white bg-white bg-contain bg-center bg-no-repeat shadow-xl sm:h-36 sm:w-36 lg:h-40 lg:w-40"
                        style={
                          company.logo_url
                            ? {
                                backgroundImage: `url("${company.logo_url}")`,
                              }
                            : undefined
                        }
                      >
                        {!company.logo_url && (
                          <Building2 size={50} className="text-blue-600" />
                        )}
                      </div>
                    </div>

                    {/* Nome, localização e informações */}
                    <div className="mt-5 min-w-0 flex-1 lg:mt-0 lg:pt-5">
                      <div className="flex items-center gap-2">
                        <h1 className="min-w-0 text-3xl font-extrabold tracking-tight text-slate-800 sm:text-4xl lg:text-[2.75rem]">
                          {company.name}
                        </h1>

                        {company.is_verified && (
                          <VerifiedCompanyBadge
                            iconOnly
                            onClick={() => setIsVerificationDetailsOpen(true)}
                            className="shrink-0 !border-0 !bg-transparent !p-0 text-blue-600"
                          />
                        )}
                      </div>

                      {company.business_type === "real_estate" &&
                        company.creci_number &&
                        company.creci_state && (
                          <div className="mt-2">
                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                              CRECI-{company.creci_state} {company.creci_number}
                            </span>
                          </div>
                        )}

                      {(company.address ||
                        company.number ||
                        company.district ||
                        company.cep ||
                        company.city ||
                        company.state) && (
                        <div className="mt-2 flex items-start gap-2 text-sm font-semibold text-slate-600">
                          <MapPin
                            size={17}
                            className="mt-0.5 shrink-0 text-slate-400"
                          />

                          <div className="min-w-0">
                            {(company.address ||
                              company.number ||
                              company.district) && (
                              <p>
                                {[
                                  [company.address, company.number]
                                    .filter(Boolean)
                                    .join(", "),
                                  company.district,
                                ]
                                  .filter(Boolean)
                                  .join(" - ")}
                              </p>
                            )}

                            {(company.cep || company.city || company.state) && (
                              <p className="mt-0.5 text-slate-500">
                                {company.cep && (
                                  <>CEP {formatCep(company.cep)}</>
                                )}

                                {company.cep &&
                                  (company.city || company.state) && (
                                    <span className="mx-1.5">•</span>
                                  )}

                                {[company.city, company.state]
                                  .filter(Boolean)
                                  .join(" - ")}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {(company.business_type !== "food" ||
                          (ratingCount > 0 && ratingAverage != null)) && (
                          <a
                            href="#avaliacoes"
                            className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-slate-700 transition hover:border-amber-300 hover:bg-amber-100"
                          >
                            <Star
                              size={15}
                              className="fill-amber-400 text-amber-400"
                            />

                            {Number(ratingAverage ?? 0).toLocaleString(
                              "pt-BR",
                              {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              },
                            )}

                            <span className="font-semibold text-slate-500">
                              ({ratingCount}{" "}
                              {ratingCount === 1 ? "avaliação" : "avaliações"})
                            </span>
                          </a>
                        )}

                        {company.business_type === "food" &&
                          company.food_availability && (
                            <span
                              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold ${
                                company.food_availability.is_open
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : company.food_availability.status ===
                                      "paused"
                                    ? "border-amber-200 bg-amber-50 text-amber-800"
                                    : "border-red-200 bg-red-50 text-red-700"
                              }`}
                            >
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  company.food_availability.is_open
                                    ? "bg-emerald-500"
                                    : company.food_availability.status ===
                                        "paused"
                                      ? "bg-amber-500"
                                      : "bg-red-500"
                                }`}
                              />

                              {formatFoodAvailabilityLabel(
                                company.food_availability,
                              )}
                            </span>
                          )}

                        {company.business_type !== "food" &&
                          company.attendance_availability && (
                            <span
                              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold ${
                                company.attendance_availability.is_open
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-red-200 bg-red-50 text-red-700"
                              }`}
                            >
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  company.attendance_availability.is_open
                                    ? "bg-emerald-500"
                                    : "bg-red-500"
                                }`}
                              />

                              {formatAttendanceAvailabilityLabel(
                                company.attendance_availability,
                              )}
                            </span>
                          )}
                      </div>
                    </div>

                    {(favoriteProductIds.length > 0 ||
                      (company.business_type === "food" &&
                        (instagramUrl.trim() || facebookUrl.trim())) ||
                      (company.business_type !== "food" &&
                        (company.whatsapps?.length ||
                          company.whatsapp))) && (
                      <div className={`flex shrink-0 items-center gap-3 ${
    company.business_type === "food"
      ? "absolute right-5 top-4 sm:right-6 lg:static lg:ml-auto lg:mt-5"
      : "mt-6 lg:ml-auto lg:mt-5"
  }`}>
                        {company.business_type === "food" && (
                          <>
                            {instagramUrl.trim() && (
                              <a
                                href={instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visitar Instagram da empresa"
                                title="Instagram"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white shadow-sm transition-transform duration-200 hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                              >
                                <FaInstagram
                                  size={19}
                                  aria-hidden="true"
                                />
                              </a>
                            )}

                            {facebookUrl.trim() && (
                              <a
                                href={facebookUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visitar Facebook da empresa"
                                title="Facebook"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1877F2] text-white shadow-sm transition-transform duration-200 hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                              >
                                <FaFacebookF
                                  size={17}
                                  aria-hidden="true"
                                />
                              </a>
                            )}
                          </>
                        )}

                        {favoriteProductIds.length > 0 && (
                          <button
                            type="button"
                            aria-label="Abrir favoritos"
                            title="Favoritos"
                            onClick={() => {
                              setCatalogView("favorites");
                              setCategoryId(null);
                              setSubcategoryId(null);

                              document
                                .getElementById("catalogo")
                                ?.scrollIntoView({ behavior: "smooth" });
                            }}
                            className="inline-flex shrink-0 items-center justify-center bg-transparent p-2 text-slate-600 transition hover:scale-110 hover:text-rose-500"
                          >
                            <Heart size={28} className="fill-none" />
                          </button>
                        )}

                        {company.business_type !== "food" &&
                          (company.whatsapps?.length || company.whatsapp) && (
                            <StoreWhatsappContact
                              company={company}
                              variant="hero"
                            />
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

          <div
            id="catalogo"
            className="mx-auto max-w-[1380px] scroll-mt-24 px-5 py-8 lg:px-8 lg:py-12"
          >
            <section className="mt-8">

<div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">



                <div>
                  <p className="text-sm font-bold text-blue-600">
                    {businessConfig.commerceMode === "lead"
                      ? "Vitrine"
                      : "Catálogo"}
                  </p>

                  <h2 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
                    {catalogView === "favorites"
                      ? "Favoritos"
                      : company.business_type === "services" &&
                          company.commerce_enabled
                        ? "Serviços e produtos"
                        : businessConfig.publicCatalogLabel}
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    {company.business_type === "services" &&
                    company.commerce_enabled
                      ? "Conheça os serviços e compre produtos na mesma loja."
                      : businessConfig.commerceMode === "lead"
                        ? `Encontre ${businessConfig.itemPluralLower} e fale diretamente com a empresa pelo WhatsApp.`
                        : `Adicione ${businessConfig.itemPluralLower} ao pedido e envie tudo pelo WhatsApp.`}
                  </p>
                </div>

                <div className="relative w-full lg:max-w-sm">
                  <Search
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar no catálogo..."
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                  />
                </div>



  </div>





              {/* Navegação do catálogo */}

                <div className="mt-6">

                {/* Desktop */}

                <div
                  className={
                    company.remove_brand && company.business_type !== "food"
                      ? "hidden"
                      : "hidden w-full items-center gap-2 border-y border-slate-200 bg-white py-2 lg:flex"
                  }
                >


                  <nav
                    id="catalog-category-navbar"
                    onMouseDown={(event) => {
                      const nav = event.currentTarget;

                      setDesktopCategorySubmenu(null);

                      nav.dataset.dragging = "1";
                      nav.dataset.didDrag = "0";
                      nav.dataset.dragStartX = String(event.clientX);
                      nav.dataset.dragStartScroll = String(nav.scrollLeft);
                    }}
                    onMouseMove={(event) => {
                      const nav = event.currentTarget;

                      if (nav.dataset.dragging !== "1") {
                        return;
                      }

                      const startX = Number(nav.dataset.dragStartX ?? 0);
                      const startScroll = Number(
                        nav.dataset.dragStartScroll ?? 0,
                      );

                      const delta = event.clientX - startX;

                      if (Math.abs(delta) > 4) {
                        nav.dataset.didDrag = "1";
                        event.preventDefault();
                      }

                      nav.scrollLeft = startScroll - delta;
                    }}
                    onMouseUp={(event) => {
                      event.currentTarget.dataset.dragging = "0";
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.dataset.dragging = "0";
                    }}
                    onClickCapture={(event) => {
                      const nav = event.currentTarget;

                      if (nav.dataset.didDrag === "1") {
                        event.preventDefault();
                        event.stopPropagation();
                        nav.dataset.didDrag = "0";
                      }
                    }}

                    className={`flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-y-hidden scroll-smooth px-1 select-none ${
                      desktopCategoryNavHasOverflow
                        ? "overflow-x-scroll overscroll-x-contain touch-pan-x pb-2 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:rgb(148_163_184)_rgb(241_245_249)] [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb:hover]:bg-slate-500 cursor-grab active:cursor-grabbing"
                        : "overflow-x-hidden pb-0"
                    }`}
                    style={{
                      WebkitOverflowScrolling: desktopCategoryNavHasOverflow
                        ? "touch"
                        : undefined,
                      scrollbarWidth: desktopCategoryNavHasOverflow
                        ? "thin"
                        : "none",
                    }}
                  >

                  {company.business_type === "food" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setCatalogView("home");
                          setCategoryId(null);
                          setSubcategoryId(null);
                        }}
                        className={`shrink-0 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${
                          catalogView === "home" &&
                          categoryId === null &&
                          subcategoryId === null
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                        }`}
                      >
                        Início
                      </button>

                      {store.promoted_products.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setCatalogView("promotions");
                            setCategoryId(null);
                            setSubcategoryId(null);

                            document
                              .getElementById("catalogo")
                              ?.scrollIntoView({
                                behavior: "smooth",
                              });
                          }}
                          className={`shrink-0 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${
                            catalogView === "promotions"
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                          }`}
                        >
                          Promoções
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                  {store.products.some((product) => product.is_featured) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCatalogView("featured");
                        setCategoryId(null);
                        setSubcategoryId(null);
                      }}
                      className={`shrink-0 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${
                        catalogView === "featured"
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                      }`}
                    >
                      Destaques
                    </button>
                  )}

                  {!store.products.some((product) => product.is_featured) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCatalogView("all");
                        setCategoryId(null);
                        setSubcategoryId(null);
                      }}
                      className={`shrink-0 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${
                        catalogView === "all" && categoryId === null
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                      }`}
                    >
                      Todos
                    </button>
                  )}
                    </>
                  )}

                  {categories.map((category) => {
                    const hasSubcategories = category.subcategories.length > 0;

                    return (
                      <div
                        key={category.id}
                        className="group relative shrink-0"
                        onMouseEnter={(event) => {
                          if (desktopCategoryCloseTimeoutRef.current) {
                            clearTimeout(
                              desktopCategoryCloseTimeoutRef.current,
                            );

                            desktopCategoryCloseTimeoutRef.current = null;
                          }

                          if (!hasSubcategories) {
                            setDesktopCategorySubmenu(null);
                            return;
                          }

                          const rect =
                            event.currentTarget.getBoundingClientRect();

                          setDesktopCategorySubmenu({
                            categoryId: category.id,
                            left: Math.max(
                              12,
                              Math.min(rect.left, window.innerWidth - 276),
                            ),
                            top: rect.bottom + 6,
                          });
                        }}
                        onMouseLeave={() => {
                          desktopCategoryCloseTimeoutRef.current =
                            setTimeout(() => {
                              setDesktopCategorySubmenu(null);
                            }, 120);
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setCatalogView("all");
                            setCategoryId(category.id);
                            setSubcategoryId(null);
                            setDesktopCategorySubmenu(null);
                          }}
                          className={`flex items-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${
                            categoryId === category.id
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                          }`}
                        >
                          <span>{category.name}</span>

                          {hasSubcategories && (
                            <ChevronDown
                              size={15}
                              className="transition-transform duration-200 group-hover:rotate-180"
                            />
                          )}
                        </button>


                      </div>
                    );
                  })}

                  {company.business_type === "vehicles" && (
                    <>
                      <button
                        type="button"
                        data-vehicle-native="financing-desktop"
                        onClick={() => setVehicleLeadMode("financing")}
                        className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950"
                      >
                        Financiamento
                      </button>

                      <button
                        type="button"
                        data-vehicle-native="appraisal-desktop"
                        onClick={() => setVehicleLeadMode("vehicle_appraisal")}
                        className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950"
                      >
                        Avalie seu carro
                      </button>
                    </>
                  )}

                  {company.business_type === "real_estate" && (
                    <button
                      type="button"
                      data-real-estate-financing="desktop"
                      onClick={() => {
                        setRealEstateFinancingProductId(null);
                        setIsRealEstateFinancingOpen(true);
                      }}
                      className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950"
                    >
                      Financiamento
                    </button>
                  )}

                  </nav>

                  {desktopCategorySubmenu &&
                    (() => {
                      const category = categories.find(
                        (item) =>
                          item.id === desktopCategorySubmenu.categoryId,
                      );

                      if (!category || category.subcategories.length === 0) {
                        return null;
                      }

                      return (
                        <div
                          data-desktop-category-submenu="true"
                          onMouseEnter={() => {
                            if (desktopCategoryCloseTimeoutRef.current) {
                              clearTimeout(
                                desktopCategoryCloseTimeoutRef.current,
                              );

                              desktopCategoryCloseTimeoutRef.current = null;
                            }
                          }}
                          onMouseLeave={() => {
                            desktopCategoryCloseTimeoutRef.current =
                              setTimeout(() => {
                                setDesktopCategorySubmenu(null);
                              }, 120);
                          }}
                          className="fixed z-[100] min-w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10"
                          style={{
                            left: desktopCategorySubmenu.left,
                            top: desktopCategorySubmenu.top,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setCatalogView("all");
                              setCategoryId(category.id);
                              setSubcategoryId(null);
                              setDesktopCategorySubmenu(null);
                            }}
                            className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-bold transition-colors ${
                              categoryId === category.id &&
                              subcategoryId === null
                                ? "bg-blue-50 text-blue-700"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            <span>Todos de {category.name}</span>

                            <span className="ml-4 text-xs font-semibold text-slate-400">
                              {category.products_count}
                            </span>
                          </button>

                          <div className="my-1 border-t border-slate-100" />

                          {category.subcategories.map((subcategory) => (
                            <button
                              type="button"
                              key={subcategory.id}
                              onClick={() => {
                                setCatalogView("all");
                                setCategoryId(category.id);
                                setSubcategoryId(subcategory.id);
                                setDesktopCategorySubmenu(null);
                              }}
                              className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                                categoryId === category.id &&
                                subcategoryId === subcategory.id
                                  ? "bg-blue-50 font-bold text-blue-700"
                                  : "font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                              }`}
                            >
                              <span>{subcategory.name}</span>

                              <span className="ml-4 text-xs font-semibold text-slate-400">
                                {subcategory.products_count}
                              </span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}



                </div>

                {/* Mobile / tablet */}
                <div className="lg:hidden">
                  <button
                    type="button"
                    onClick={() => setIsCatalogMenuOpen((current) => !current)}
                    aria-expanded={isCatalogMenuOpen}
                    className="flex h-12 w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <Menu size={20} className="shrink-0 text-blue-600" />

                      <span className="truncate">
                        {selectedCategory
                          ? selectedCategory.name
                          : "Categorias"}

                        {selectedCategory && subcategoryId !== null && (
                          <>
                            {" / "}
                            {
                              selectedCategory.subcategories.find(
                                (subcategory) =>
                                  subcategory.id === subcategoryId,
                              )?.name
                            }
                          </>
                        )}
                      </span>
                    </span>

                    <ChevronDown
                      size={18}
                      className={`shrink-0 text-slate-400 transition-transform ${
                        isCatalogMenuOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isCatalogMenuOpen && (
                    <div className="mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5">
                      {company.business_type === "food" ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCatalogView("home");
                            setCategoryId(null);
                            setSubcategoryId(null);
                            setMobileExpandedCategoryId(null);
                            setIsCatalogMenuOpen(false);
                          }}
                          className={`flex w-full items-center justify-between border-b border-slate-100 px-4 py-3.5 text-left text-sm font-bold ${
                            catalogView === "home" &&
                            categoryId === null &&
                            subcategoryId === null
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700"
                          }`}
                        >
                          Início
                        </button>
                      ) : (
                        <>
                      {store.products.some(
                        (product) => product.is_featured,
                      ) && (
                        <button
                          type="button"
                          onClick={() => {
                            setCatalogView("featured");
                            setCategoryId(null);
                            setSubcategoryId(null);
                            setMobileExpandedCategoryId(null);
                            setIsCatalogMenuOpen(false);
                          }}
                          className={`flex w-full items-center justify-between border-b border-slate-100 px-4 py-3.5 text-left text-sm font-bold ${
                            catalogView === "featured"
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700"
                          }`}
                        >
                          Destaques
                        </button>
                      )}

                      {!store.products.some(
                        (product) => product.is_featured,
                      ) && (
                        <button
                          type="button"
                          onClick={() => {
                            setCatalogView("all");
                            setCategoryId(null);
                            setSubcategoryId(null);
                            setMobileExpandedCategoryId(null);
                            setIsCatalogMenuOpen(false);
                          }}
                          className={`flex w-full items-center justify-between border-b border-slate-100 px-4 py-3.5 text-left text-sm font-bold ${
                            catalogView === "all" && categoryId === null
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700"
                          }`}
                        >
                          {company.business_type === "services"
                            ? company.commerce_enabled
                              ? "Todos os itens"
                              : "Todos os serviços"
                            : "Todos os produtos"}
                        </button>
                      )}
                        </>
                      )}

                      {company.business_type === "food" &&
                        store.promoted_products.length > 0 && (
                          <button
                            type="button"
                            data-food-mobile-menu-promotions="true"
                            onClick={() => {
                              setCatalogView("promotions");
                              setCategoryId(null);
                              setSubcategoryId(null);
                              setMobileExpandedCategoryId(null);
                              setIsCatalogMenuOpen(false);

                              window.setTimeout(() => {
                                document
                                  .getElementById("catalogo")
                                  ?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                  });
                              }, 0);
                            }}
                            className={`flex w-full items-center justify-between border-b border-slate-100 px-4 py-3.5 text-left text-sm font-bold ${
                              catalogView === "promotions"
                                ? "bg-blue-50 text-blue-700"
                                : "text-slate-700"
                            }`}
                          >
                            <span>Promoções</span>
                            <span className="text-xs font-semibold text-slate-400">
                              {store.promoted_products.length}
                            </span>
                          </button>
                        )}

                      {categories.map((category) => {
                        const hasSubcategories =
                          category.subcategories.length > 0;

                        const expanded =
                          mobileExpandedCategoryId === category.id;

                        return (
                          <div
                            key={category.id}
                            className="border-b border-slate-100 last:border-b-0"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                if (hasSubcategories) {
                                  setMobileExpandedCategoryId(
                                    expanded ? null : category.id,
                                  );

                                  return;
                                }

                                setCatalogView("all");
                                setCategoryId(category.id);
                                setSubcategoryId(null);
                                setIsCatalogMenuOpen(false);
                              }}
                              className={`flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-sm font-bold ${
                                categoryId === category.id
                                  ? "text-blue-700"
                                  : "text-slate-700"
                              }`}
                            >
                              <span>{category.name}</span>

                              <span className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-slate-400">
                                  {category.products_count}
                                </span>

                                {hasSubcategories && (
                                  <ChevronDown
                                    size={17}
                                    className={`text-slate-400 transition-transform ${
                                      expanded ? "rotate-180" : ""
                                    }`}
                                  />
                                )}
                              </span>
                            </button>

                            {hasSubcategories && expanded && (
                              <div className="border-t border-slate-100 bg-slate-50 px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCatalogView("all");
                                    setCategoryId(category.id);
                                    setSubcategoryId(null);
                                    setIsCatalogMenuOpen(false);
                                  }}
                                  className={`w-full rounded-xl px-4 py-3 text-left text-sm font-semibold ${
                                    categoryId === category.id &&
                                    subcategoryId === null
                                      ? "bg-blue-50 text-blue-700"
                                      : "text-slate-600"
                                  }`}
                                >
                                  Todos de {category.name}
                                </button>

                                {category.subcategories.map((subcategory) => (
                                  <button
                                    type="button"
                                    key={subcategory.id}
                                    onClick={() => {
                                      setCatalogView("all");
                                      setCategoryId(category.id);
                                      setSubcategoryId(subcategory.id);
                                      setIsCatalogMenuOpen(false);
                                    }}
                                    className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm ${
                                      categoryId === category.id &&
                                      subcategoryId === subcategory.id
                                        ? "bg-blue-50 font-bold text-blue-700"
                                        : "font-medium text-slate-600"
                                    }`}
                                  >
                                    <span>{subcategory.name}</span>

                                    <span className="text-xs font-semibold text-slate-400">
                                      {subcategory.products_count}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {company.business_type === "vehicles" && (
                        <>
                          <button
                            type="button"
                            data-vehicle-native="financing-mobile"
                            onClick={() => {
                              setMobileExpandedCategoryId(null);
                              setIsCatalogMenuOpen(false);
                              setVehicleLeadMode("financing");
                            }}
                            className="flex w-full items-center border-b border-slate-100 px-4 py-3.5 text-left text-sm font-bold text-slate-700"
                          >
                            Financiamento
                          </button>

                          <button
                            type="button"
                            data-vehicle-native="appraisal-mobile"
                            onClick={() => {
                              setMobileExpandedCategoryId(null);
                              setIsCatalogMenuOpen(false);
                              setVehicleLeadMode("vehicle_appraisal");
                            }}
                            className="flex w-full items-center px-4 py-3.5 text-left text-sm font-bold text-slate-700"
                          >
                            Avalie seu carro
                          </button>
                        </>
                      )}

                      {company.business_type === "real_estate" && (
                        <button
                          type="button"
                          data-real-estate-financing="mobile"
                          onClick={() => {
                            setMobileExpandedCategoryId(null);
                            setIsCatalogMenuOpen(false);
                            setRealEstateFinancingProductId(null);
                            setIsRealEstateFinancingOpen(true);
                          }}
                          className="flex w-full items-center px-4 py-3.5 text-left text-sm font-bold text-slate-700"
                        >
                          Financiamento
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {(!(company.remove_brand || company.business_type === "food") ||
                store.products.length === 0 ||
                catalogView !== "home" ||
                search.trim()) && (
                <>
                  {promotionCatalogProducts.length === 0 ? (
                    <div className="mt-8 flex min-h-72 flex-col items-center justify-center rounded-3xl border border-slate-200 bg-white p-8 text-center">
                      <Package size={38} className="text-slate-300" />

                      <h3 className="mt-4 text-lg font-bold text-slate-900">
                        Nenhum {businessConfig.itemSingularLower} encontrado
                      </h3>

                      <p className="mt-2 text-sm text-slate-500">
                        {store.products.length === 0
                          ? company.business_type === "food"
                            ? "O cardápio está sendo preparado. Volte em breve."
                            : "O catálogo está sendo preparado. Volte em breve."
                          : "Tente buscar outro termo ou selecionar outra categoria."}
                      </p>
                    </div>
                  ) : (
                    <>
                      {company.business_type === "food" && (
                        <div className="mt-6 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white sm:hidden">
                          {promotionCatalogProducts.map((product) => (
                            <FoodProductCard catalogOnly={!showCart}
                              key={product.id}
                              product={product}
                              orderingDisabled={
                                company.business_type === "food" &&
                                company.food_availability?.is_open === false
                              }
                              quantity={cart
                                .filter(
                                  (item) => item.product.id === product.id,
                                )
                                .reduce((sum, item) => sum + item.quantity, 0)}
                              onOpen={() => {
                                trackProductViewOncePerSession(
                                  company.slug,
                                  product.id,
                                );

                                openProductDetails(product);
                              }}
                              onAdd={() => addToCart(product)}
                              onOpenCart={() => setIsCartOpen(true)}
                            />
                          ))}
                        </div>
                      )}

                      {company.business_type === "food" &&
                        catalogView === "featured" &&
                        promotionCatalogProducts.length > 4 && (
                          <div className="mb-3 hidden justify-end gap-2 sm:flex">
                            <button
                              type="button"
                              aria-label="Destaques anteriores"
                              onClick={() => {
                                document
                                  .getElementById("food-featured-main-carousel")
                                  ?.scrollBy({
                                    left: -900,
                                    behavior: "smooth",
                                  });
                              }}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                            >
                              <ChevronLeft size={20} />
                            </button>

                            <button
                              type="button"
                              aria-label="Próximos destaques"
                              onClick={() => {
                                document
                                  .getElementById("food-featured-main-carousel")
                                  ?.scrollBy({
                                    left: 900,
                                    behavior: "smooth",
                                  });
                              }}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                            >
                              <ChevronRight size={20} />
                            </button>
                          </div>
                        )}

                      <div
                        id={
                          company.business_type === "food" &&
                          catalogView === "featured"
                            ? "food-featured-main-carousel"
                            : undefined
                        }
                        className={`mt-8 grid gap-6 ${
                          company.business_type === "food"
                            ? catalogView === "featured"
                              ? "hidden sm:grid sm:grid-flow-col sm:auto-cols-[calc((100%-3rem)/3)] sm:overflow-x-auto sm:scroll-smooth sm:snap-x sm:snap-mandatory sm:overscroll-x-contain sm:[scrollbar-width:none] sm:[&::-webkit-scrollbar]:hidden xl:auto-cols-[calc((100%-4.5rem)/4)]"
                              : "hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                            : "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                        }`}
                      >
                        {(
                          company.business_type === "food" &&
                          catalogView === "featured"
                            ? promotionCatalogProducts.slice(0, 16)
                            : promotionCatalogProducts
                        ).map((product, index) => (
                          <Fragment key={product.id}>
                            {company.business_type === "services" &&
                              company.commerce_enabled &&
                              (index === 0 ||
                                promotionCatalogProducts[index - 1].item_kind !==
                                  product.item_kind) && (
                                <h3 className="col-span-full text-xl font-black text-slate-900">
                                  {product.item_kind === "service"
                                    ? "Serviços"
                                    : "Produtos para comprar"}
                                </h3>
                              )}
                            <ProductCard catalogOnly={company.business_type === "food" && !showCart}
                              product={product}
                              orderingDisabled={
                                company.business_type === "food" &&
                                company.food_availability?.is_open === false
                              }
                              businessConfig={itemConfig(product)}
                              location={
                                company.business_type === "real_estate"
                                  ? [
                                      product.business_attributes?.neighborhood,
                                      product.business_attributes?.city,
                                      company.state,
                                    ]
                                      .filter(Boolean)
                                      .join(" - ")
                                  : [company.city, company.state]
                                      .filter(Boolean)
                                      .join(" - ")
                              }
                              companySlug={company.slug}
                              isFavorite={favoriteProductIds.includes(
                                product.id,
                              )}
                              onToggleFavorite={() =>
                                toggleFavoriteProduct(product.id)
                              }
                              quantity={cart
                                .filter(
                                  (item) => item.product.id === product.id,
                                )
                                .reduce((sum, item) => sum + item.quantity, 0)}
                              onOpen={() => {
                                trackProductViewOncePerSession(
                                  company.slug,
                                  product.id,
                                );

                                openProductDetails(product);
                              }}
                              onAdd={() => addToCart(product)}
                              onOpenCart={() => setIsCartOpen(true)}
                              onFinancing={() => {
                                if (company.business_type === "real_estate") {
                                  setRealEstateFinancingProductId(product.id);
                                  setIsRealEstateFinancingOpen(true);
                                  return;
                                }

                                setVehicleLeadMode("financing");
                              }}
                            />
                          </Fragment>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
              {!search.trim() &&
                categoryId === null &&
                subcategoryId === null &&
                (catalogView === "home" || catalogView === "all") &&
                !(
                  (company.remove_brand || company.business_type === "food") &&
                  catalogView === "home"
                ) && (
                  <StorefrontAdBanner
                    campaign={campaignBanners?.after_featured}
                    onProducts={openCampaignProducts}
                    onLead={
                      campaignLeadAllowed
                        ? () => {
                            const campaign = campaignBanners?.after_featured;
                            if (campaign) setCampaignInterest(campaign);
                          }
                        : undefined
                    }
                    className="mt-10"
                  />
                )}

              {(company.remove_brand || company.business_type === "food") &&
                catalogView === "home" &&
                !search.trim() && (
                  <>
                    {store.products.some((product) => product.is_featured) && (
                      <section
                        className={`${
                          store.most_viewed.length > 0
                            ? "mt-14 border-t border-slate-200 pt-14"
                            : ""
                        }`}
                      >
                        <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600">
                          Seleção da empresa
                        </p>

                        <div className="flex items-center justify-between gap-4">
                          <h2 className="mt-2 text-2xl font-black text-slate-950 sm:text-3xl">
                            Destaques
                          </h2>

                          {company.business_type === "food" && (
                            <div className="hidden items-center gap-2 sm:flex">
                              <button
                                type="button"
                                aria-label="Voltar produtos em destaque"
                                onClick={() => {
                                  document
                                    .getElementById("food-featured-carousel")
                                    ?.scrollBy({
                                      left: -360,
                                      behavior: "smooth",
                                    });
                                }}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-2xl font-semibold leading-none text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                              >
                                ‹
                              </button>

                              <button
                                type="button"
                                aria-label="Avançar produtos em destaque"
                                onClick={() => {
                                  document
                                    .getElementById("food-featured-carousel")
                                    ?.scrollBy({
                                      left: 360,
                                      behavior: "smooth",
                                    });
                                }}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-2xl font-semibold leading-none text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                              >
                                ›
                              </button>
                            </div>
                          )}
                        </div>

                        <p className="mt-2 text-sm text-slate-500">
                          {company.business_type === "services"
                            ? company.commerce_enabled
                              ? "Itens selecionados especialmente para você."
                              : "Serviços selecionados especialmente para você."
                            : "Produtos selecionados especialmente para você."}
                        </p>

                        {company.business_type === "food" && (
                          <div className="mt-7 sm:hidden">
                            <div
                              id="food-featured-mobile-carousel"
                              className="food-mobile-scrollbar-hidden flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                            >
                              {store.products
                                .filter((product) => product.is_featured)
                                .slice(0, 8)
                                .map((product) => (
                                  <FoodCompactProductCard
                                    key={`featured-food-${product.id}`}
                                    product={product}
                                    catalogOnly={!showCart}
                                    orderingDisabled={
                                      company.food_availability?.is_open === false
                                    }
                                    quantity={cart
                                      .filter(
                                        (item) =>
                                          item.product.id === product.id,
                                      )
                                      .reduce(
                                        (sum, item) =>
                                          sum + item.quantity,
                                        0,
                                      )}
                                    onOpen={() => {
                                      trackProductViewOncePerSession(
                                        company.slug,
                                        product.id,
                                      );
                                      openProductDetails(product);
                                    }}
                                    onAdd={() => addToCart(product)}
                                    onOpenCart={() => setIsCartOpen(true)}
                                  />
                                ))}
                            </div>

                            {store.products.filter((product) => product.is_featured).length > 1 && (
                              <div className="mt-3 flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  aria-label="Voltar destaques"
                                  onClick={() => {
                                    document
                                      .getElementById("food-featured-mobile-carousel")
                                      ?.scrollBy({
                                        left: -180,
                                        behavior: "smooth",
                                      });
                                  }}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-xl font-semibold leading-none text-slate-700 shadow-sm"
                                >
                                  ‹
                                </button>
                                <button
                                  type="button"
                                  aria-label="Avançar destaques"
                                  onClick={() => {
                                    document
                                      .getElementById("food-featured-mobile-carousel")
                                      ?.scrollBy({
                                        left: 180,
                                        behavior: "smooth",
                                      });
                                  }}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-xl font-semibold leading-none text-slate-700 shadow-sm"
                                >
                                  ›
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {company.business_type === "food" && (
                          <div className="relative hidden sm:block">
                            <div
                              id="food-featured-carousel"
                              className="mt-7 grid grid-flow-col auto-cols-[calc((100%-3rem)/3)] gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden xl:auto-cols-[calc((100%-4.5rem)/4)] cursor-grab active:cursor-grabbing"

                              onMouseDown={(event) => {
                                const carousel = event.currentTarget;

                                carousel.dataset.dragging = "1";
                                carousel.dataset.didDrag = "0";
                                carousel.dataset.dragStartX = String(
                                  event.clientX,
                                );
                                carousel.dataset.dragStartScroll = String(
                                  carousel.scrollLeft,
                                );
                              }}
                              onMouseMove={(event) => {
                                const carousel = event.currentTarget;

                                if (carousel.dataset.dragging !== "1") {
                                  return;
                                }

                                const startX = Number(
                                  carousel.dataset.dragStartX ?? 0,
                                );

                                const startScroll = Number(
                                  carousel.dataset.dragStartScroll ?? 0,
                                );

                                const delta = event.clientX - startX;

                                if (Math.abs(delta) > 4) {
                                  carousel.dataset.didDrag = "1";
                                  event.preventDefault();
                                }

                                carousel.scrollLeft =
                                  startScroll - delta;
                              }}
                              onMouseUp={(event) => {
                                event.currentTarget.dataset.dragging = "0";
                              }}
                              onMouseLeave={(event) => {
                                event.currentTarget.dataset.dragging = "0";
                              }}
                              onClickCapture={(event) => {
                                const carousel = event.currentTarget;

                                if (
                                  carousel.dataset.didDrag === "1"
                                ) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  carousel.dataset.didDrag = "0";
                                }
                              }}
                              style={{
                                scrollbarWidth: "none",
                                WebkitOverflowScrolling: "touch",
                              }}
>
                              {store.products
                                .filter((product) => product.is_featured)
                                .slice(0, 16)
                                .map((product) => (
                                  <div
                                    key={`featured-food-desktop-${product.id}`}
                                    className="min-w-0 snap-start"
                                  >
                                    <ProductCard catalogOnly={company.business_type === "food" && !showCart}
                                      product={product}
                                      orderingDisabled={
                                        company.business_type === "food" &&
                                        company.food_availability?.is_open === false
                                      }
                                      businessConfig={itemConfig(product)}
                                      location={[company.city, company.state]
                                        .filter(Boolean)
                                        .join(" - ")}
                                      companySlug={company.slug}
                                      isFavorite={favoriteProductIds.includes(
                                        product.id,
                                      )}
                                      onToggleFavorite={() =>
                                        toggleFavoriteProduct(product.id)
                                      }
                                      quantity={cart
                                        .filter(
                                          (item) =>
                                            item.product.id === product.id,
                                        )
                                        .reduce(
                                          (sum, item) =>
                                            sum + item.quantity,
                                          0,
                                        )}
                                      onOpen={() => {
                                        trackProductViewOncePerSession(
                                          company.slug,
                                          product.id,
                                        );

                                        openProductDetails(product);
                                      }}
                                      onAdd={() => addToCart(product)}
                                      onOpenCart={() =>
                                        setIsCartOpen(true)
                                      }
                                      onFinancing={() => {
                                        if (
                                          company.business_type ===
                                          "real_estate"
                                        ) {
                                          setRealEstateFinancingProductId(
                                            product.id,
                                          );
                                          setIsRealEstateFinancingOpen(true);
                                          return;
                                        }

                                        if (
                                          company.business_type ===
                                          "vehicles"
                                        ) {
                                          setVehicleLeadMode("financing");
                                        }
                                      }}
                                    />
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        <div
                          className={`mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
                            company.business_type === "food"
                              ? "hidden"
                              : ""
                          }`}
                        >
                          {store.products
                            .filter((product) => product.is_featured)
                            .slice(0, 8)
                            .map((product) => (
                              <ProductCard catalogOnly={company.business_type === "food" && !showCart}
                                key={`featured-home-${product.id}`}
                                product={product}
                                orderingDisabled={
                                  company.business_type === "food" &&
                                  company.food_availability?.is_open === false
                                }
                                businessConfig={itemConfig(product)}
                                location={
                                  company.business_type === "real_estate"
                                    ? [
                                        product.business_attributes
                                          ?.neighborhood,
                                        product.business_attributes?.city,
                                        company.state,
                                      ]
                                        .filter(Boolean)
                                        .join(" - ")
                                    : [company.city, company.state]
                                        .filter(Boolean)
                                        .join(" - ")
                                }
                                companySlug={company.slug}
                                isFavorite={favoriteProductIds.includes(
                                  product.id,
                                )}
                                onToggleFavorite={() =>
                                  toggleFavoriteProduct(product.id)
                                }
                                quantity={cart
                                  .filter(
                                    (item) => item.product.id === product.id,
                                  )
                                  .reduce(
                                    (sum, item) => sum + item.quantity,
                                    0,
                                  )}
                                onOpen={() => {
                                  trackProductViewOncePerSession(
                                    company.slug,
                                    product.id,
                                  );

                                  openProductDetails(product);
                                }}
                                onAdd={() => addToCart(product)}
                                onOpenCart={() => setIsCartOpen(true)}
                                onFinancing={() => {
                                  if (company.business_type === "real_estate") {
                                    setRealEstateFinancingProductId(product.id);
                                    setIsRealEstateFinancingOpen(true);
                                    return;
                                  }

                                  setVehicleLeadMode("financing");
                                }}
                              />
                            ))}
                        </div>
                      </section>
                    )}
                    <StorefrontAdBanner
                      campaign={campaignBanners?.after_featured}
                      onProducts={openCampaignProducts}
                      onLead={
                        campaignLeadAllowed
                          ? () => {
                              const campaign = campaignBanners?.after_featured;
                              if (campaign) setCampaignInterest(campaign);
                            }
                          : undefined
                      }
                      className="mt-10"
                    />

                    {categoryId === null &&
                    subcategoryId === null &&
                    !search.trim() &&
                    (company.business_type === "food"
                      ? store.best_sellers.length > 0
                      : store.most_viewed.length > 0) && (
                      <section className="mt-14 border-t border-slate-200 pt-14">
                        <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600">
                          {company.business_type === "food"
                            ? "Os campeões de venda"
                            : "Preferidos dos visitantes"}
                        </p>

                        <div className="mt-2 flex items-center justify-between gap-4">
                          <h2 className="text-2xl font-black text-slate-950 sm:text-3xl">
                            {company.business_type === "food"
                              ? "Mais pedidos"
                              : "Mais acessados"}
                          </h2>

                          {company.business_type === "food" &&
                            store.best_sellers.length > 1 && (
                              <div className="hidden items-center gap-2 sm:flex">
                                <button
                                  type="button"
                                  aria-label="Voltar produtos mais pedidos"
                                  onClick={() => {
                                    document
                                      .getElementById("food-most-ordered-carousel")
                                      ?.scrollBy({
                                        left: -360,
                                        behavior: "smooth",
                                      });
                                  }}
                                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-2xl font-semibold leading-none text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                                >
                                  ‹
                                </button>

                                <button
                                  type="button"
                                  aria-label="Avançar produtos mais pedidos"
                                  onClick={() => {
                                    document
                                      .getElementById("food-most-ordered-carousel")
                                      ?.scrollBy({
                                        left: 360,
                                        behavior: "smooth",
                                      });
                                  }}
                                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-2xl font-semibold leading-none text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                                >
                                  ›
                                </button>
                              </div>
                            )}
                        </div>

                        <p className="mt-2 text-sm text-slate-500">
                          {company.business_type === "services"
                            ? company.commerce_enabled
                              ? "Os itens que mais despertam interesse nesta loja."
                              : "Os serviços que mais despertam interesse nesta loja."
                            : "Os produtos que mais despertam interesse nesta loja."}
                        </p>

                        {company.business_type === "food" && (
                          <div className="mt-6 sm:hidden">
                            <div
                              id="food-most-ordered-mobile-carousel"
                              className="food-mobile-scrollbar-hidden flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                            >
                              {store.best_sellers.map((product) => (
                                <FoodCompactProductCard
                                  key={`most-viewed-food-${product.id}`}
                                  product={product}
                                  catalogOnly={!showCart}
                                  orderingDisabled={
                                    company.food_availability?.is_open === false
                                  }
                                  quantity={cart
                                    .filter(
                                      (item) =>
                                        item.product.id === product.id,
                                    )
                                    .reduce(
                                      (sum, item) =>
                                        sum + item.quantity,
                                      0,
                                    )}
                                  onOpen={() => {
                                    trackProductViewOncePerSession(
                                      company.slug,
                                      product.id,
                                    );

                                    openProductDetails(product);
                                  }}
                                  onAdd={() => addToCart(product)}
                                  onOpenCart={() =>
                                    setIsCartOpen(true)
                                  }
                                />
                              ))}
                            </div>

                            {store.best_sellers.length > 1 && (
                              <div className="mt-3 flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  aria-label="Voltar produtos mais pedidos"
                                  onClick={() => {
                                    document
                                      .getElementById(
                                        "food-most-ordered-mobile-carousel",
                                      )
                                      ?.scrollBy({
                                        left: -180,
                                        behavior: "smooth",
                                      });
                                  }}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-xl font-semibold leading-none text-slate-700 shadow-sm"
                                >
                                  ‹
                                </button>

                                <button
                                  type="button"
                                  aria-label="Avançar produtos mais pedidos"
                                  onClick={() => {
                                    document
                                      .getElementById(
                                        "food-most-ordered-mobile-carousel",
                                      )
                                      ?.scrollBy({
                                        left: 180,
                                        behavior: "smooth",
                                      });
                                  }}
                                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-xl font-semibold leading-none text-slate-700 shadow-sm"
                                >
                                  ›
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <div
                          id={
                            company.business_type === "food"
                              ? "food-most-ordered-carousel"
                              : undefined
                          }
                          className={
                            company.business_type === "food"
                              ? "mt-7 hidden gap-6 overflow-x-hidden sm:flex sm:snap-x sm:snap-mandatory sm:[&>*]:w-[calc((100%-1.5rem)/2)] sm:[&>*]:shrink-0 sm:[&>*]:snap-start lg:[&>*]:w-[calc((100%-3rem)/3)] xl:[&>*]:w-[calc((100%-4.5rem)/4)]"
                              : "mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                          }
                        >
                          {(company.business_type === "food"
                            ? store.best_sellers
                            : store.most_viewed
                          ).map((product) => (
                            <ProductCard catalogOnly={company.business_type === "food" && !showCart}
                              key={`most-viewed-${product.id}`}
                              product={product}
                              orderingDisabled={
                                company.business_type === "food" &&
                                company.food_availability?.is_open === false
                              }
                              businessConfig={itemConfig(product)}
                              location={
                                company.business_type === "real_estate"
                                  ? [
                                      product.business_attributes?.neighborhood,
                                      product.business_attributes?.city,
                                      company.state,
                                    ]
                                      .filter(Boolean)
                                      .join(" - ")
                                  : [company.city, company.state]
                                      .filter(Boolean)
                                      .join(" - ")
                              }
                              companySlug={company.slug}
                              isFavorite={favoriteProductIds.includes(
                                product.id,
                              )}
                              onToggleFavorite={() =>
                                toggleFavoriteProduct(product.id)
                              }
                              quantity={cart
                                .filter(
                                  (item) => item.product.id === product.id,
                                )
                                .reduce((sum, item) => sum + item.quantity, 0)}
                              onOpen={() => {
                                trackProductViewOncePerSession(
                                  company.slug,
                                  product.id,
                                );

                                openProductDetails(product);
                              }}
                              onAdd={() => addToCart(product)}
                              onOpenCart={() => setIsCartOpen(true)}
                              onFinancing={() => {
                                if (company.business_type === "real_estate") {
                                  setRealEstateFinancingProductId(product.id);
                                  setIsRealEstateFinancingOpen(true);
                                  return;
                                }

                                setVehicleLeadMode("financing");
                              }}
                            />
                          ))}
                        </div>
                      </section>
                    )}
                  </>
                )}

              {(company.remove_brand || company.business_type === "food") &&
                catalogView === "home" &&
                categoryId === null &&
                subcategoryId === null &&
                !search.trim() && (
                  <StorefrontAdBanner
                    campaign={
                      campaignBanners?.between_most_viewed_and_categories
                    }
                    onProducts={openCampaignProducts}
                    onLead={
                      campaignLeadAllowed
                        ? () => {
                            const campaign =
                              campaignBanners?.between_most_viewed_and_categories;
                            if (campaign) setCampaignInterest(campaign);
                          }
                        : undefined
                    }
                    className="mt-10"
                  />
                )}

              {/* PREMIUM_CATEGORY_SECTIONS */}
              {(company.remove_brand || company.business_type === "food") &&
                catalogView === "home" &&
                categoryId === null &&
                subcategoryId === null &&
                !search.trim() && (
                  <div className="mt-14 border-t border-slate-200 pt-14">
                    <div className="space-y-12">
                      {categories
                        .filter((category) =>
                          store.products.some(
                            (product) => product.category === category.id,
                          ),
                        )
                        .map((category) => {
                          const categoryProducts = store.products
                            .filter(
                              (product) => product.category === category.id,
                            )
                            .slice(0, 6);

                          if (categoryProducts.length === 0) {
                            return null;
                          }

                          return (
                            <section key={`home-category-${category.id}`}>
                              <div className="mb-5 flex items-end justify-between gap-4">
                                <div className="min-w-0">
                                  <h2 className="text-2xl font-black text-slate-950">
                                    {category.name}
                                  </h2>

                                  {category.description && (
                                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                                      {category.description}
                                    </p>
                                  )}
                                </div>

                                {category.products_count >
                                  categoryProducts.length && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCatalogView("all");
                                      setCategoryId(category.id);
                                      setSubcategoryId(null);

                                      window.setTimeout(() => {
                                        document
                                          .getElementById("catalogo")
                                          ?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                          });
                                      }, 0);
                                    }}
                                    className="shrink-0 text-sm font-bold text-blue-600 transition hover:text-blue-700"
                                  >
                                    Ver todos
                                  </button>
                                )}
                              </div>

                              {company.business_type === "food" && (
                                <div className="sm:hidden grid grid-cols-1 gap-3">
                                  {categoryProducts.map((product) => (
                                    <FoodProductCard catalogOnly={!showCart}
                                      key={`food-home-category-${category.id}-${product.id}`}
                                      product={product}
                                      orderingDisabled={
                                        company.business_type === "food" &&
                                        company.food_availability?.is_open === false
                                      }
                                      quantity={cart
                                        .filter(
                                          (item) =>
                                            item.product.id === product.id,
                                        )
                                        .reduce(
                                          (sum, item) => sum + item.quantity,
                                          0,
                                        )}
                                      onOpen={() => {
                                        trackProductViewOncePerSession(
                                          company.slug,
                                          product.id,
                                        );

                                        openProductDetails(product);
                                      }}
                                      onAdd={() => addToCart(product)}
                                      onOpenCart={() => setIsCartOpen(true)}
                                    />
                                  ))}
                                </div>
                              )}

                              {/* CATEGORY_DESKTOP_SAME_AS_BEST_SELLERS */}
                              <div
                                className={`mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
                                  company.business_type === "food"
                                    ? "hidden sm:grid"
                                    : ""
                                }`}
                              >
                                {categoryProducts.map((product) => (
                                  <ProductCard catalogOnly={company.business_type === "food" && !showCart}
                                    key={`category-${product.id}`}
                                    product={product}
                                    orderingDisabled={
                                      company.business_type === "food" &&
                                      company.food_availability?.is_open === false
                                    }
                                    businessConfig={itemConfig(product)}
                                    location={
                                      company.business_type === "real_estate"
                                        ? [
                                            product.business_attributes
                                              ?.neighborhood,
                                            product.business_attributes?.city,
                                            company.state,
                                          ]
                                            .filter(Boolean)
                                            .join(" - ")
                                        : [company.city, company.state]
                                            .filter(Boolean)
                                            .join(" - ")
                                    }
                                    companySlug={company.slug}
                                    isFavorite={favoriteProductIds.includes(
                                      product.id,
                                    )}
                                    onToggleFavorite={() =>
                                      toggleFavoriteProduct(product.id)
                                    }
                                    quantity={cart
                                      .filter(
                                        (item) =>
                                          item.product.id === product.id,
                                      )
                                      .reduce(
                                        (sum, item) => sum + item.quantity,
                                        0,
                                      )}
                                    onOpen={() => {
                                      trackProductViewOncePerSession(
                                        company.slug,
                                        product.id,
                                      );

                                      openProductDetails(product);
                                    }}
                                    onAdd={() => addToCart(product)}
                                    onOpenCart={() => setIsCartOpen(true)}
                                    onFinancing={() => {
                                      if (
                                        company.business_type === "real_estate"
                                      ) {
                                        setRealEstateFinancingProductId(
                                          product.id,
                                        );
                                        setIsRealEstateFinancingOpen(true);
                                        return;
                                      }

                                      setVehicleLeadMode("financing");
                                    }}
                                  />
                                ))}
                              </div>
                            </section>
                          );
                        })}
                    </div>
                  </div>
                )}

              {(!(company.remove_brand || company.business_type === "food") ||
                catalogView !== "home") &&
                showCart &&
                store.best_sellers.length > 0 && (
                  <section
                    id="mais-pedidos"
                    className="mt-14 border-t border-slate-200 pt-14"
                  >
                    <div>
                      <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600">
                        Preferidos dos clientes
                      </p>

                      <div className="mt-2 flex items-end justify-between gap-4">
                        <div>
                          <h2 className="text-2xl font-black text-slate-950 sm:text-3xl">
                            Mais pedidos
                          </h2>

                          <p className="mt-2 text-sm text-slate-500">
                            {company.business_type === "services" &&
                            company.commerce_enabled
                              ? "Os itens que mais fazem sucesso nesta loja."
                              : "Os produtos que mais fazem sucesso nesta loja."}
                          </p>
                        </div>
                      </div>

                      {company.business_type === "food" && (
                        <div className="mt-7 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white sm:hidden">
                          {store.best_sellers.map((product) => (
                            <FoodProductCard catalogOnly={!showCart}
                              key={`best-seller-food-${product.id}`}
                              product={product}
                              orderingDisabled={
                                company.business_type === "food" &&
                                company.food_availability?.is_open === false
                              }
                              quantity={cart
                                .filter(
                                  (item) => item.product.id === product.id,
                                )
                                .reduce((sum, item) => sum + item.quantity, 0)}
                              onOpen={() => {
                                trackProductViewOncePerSession(
                                  company.slug,
                                  product.id,
                                );

                                openProductDetails(product);
                              }}
                              onAdd={() => addToCart(product)}
                              onOpenCart={() => setIsCartOpen(true)}
                            />
                          ))}
                        </div>
                      )}

                      <div
                        className={`mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
                          company.business_type === "food"
                            ? "hidden sm:grid"
                            : ""
                        }`}
                      >
                        {store.best_sellers.map((product) => (
                          <ProductCard catalogOnly={company.business_type === "food" && !showCart}
                            key={`best-seller-${product.id}`}
                            product={product}
                            orderingDisabled={
                              company.business_type === "food" &&
                              company.food_availability?.is_open === false
                            }
                            businessConfig={itemConfig(product)}
                            location={
                              company.business_type === "real_estate"
                                ? [
                                    product.business_attributes?.neighborhood,
                                    product.business_attributes?.city,
                                    company.state,
                                  ]
                                    .filter(Boolean)
                                    .join(" - ")
                                : [company.city, company.state]
                                    .filter(Boolean)
                                    .join(" - ")
                            }
                            companySlug={company.slug}
                            isFavorite={favoriteProductIds.includes(product.id)}
                            onToggleFavorite={() =>
                              toggleFavoriteProduct(product.id)
                            }
                            quantity={cart
                              .filter((item) => item.product.id === product.id)
                              .reduce((sum, item) => sum + item.quantity, 0)}
                            onOpen={() => {
                              trackProductViewOncePerSession(
                                company.slug,
                                product.id,
                              );

                              openProductDetails(product);
                            }}
                            onAdd={() => addToCart(product)}
                            onOpenCart={() => setIsCartOpen(true)}
                            onFinancing={() => {
                              if (company.business_type === "real_estate") {
                                setRealEstateFinancingProductId(product.id);
                                setIsRealEstateFinancingOpen(true);
                                return;
                              }

                              setVehicleLeadMode("financing");
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </section>
                )}

              {!company.remove_brand &&
                catalogView === "featured" &&
                categoryId === null &&
                subcategoryId === null && (
                  <div className="mt-14 border-t border-slate-200 pt-14">
                    <div className="space-y-12">
                      {categories
                        .filter((category) =>
                          store.products.some(
                            (product) =>
                              product.category === category.id &&
                              !product.is_featured,
                          ),
                        )
                        .slice(0, 6)
                        .map((category) => {
                          const categoryProducts = store.products
                            .filter(
                              (product) =>
                                product.category === category.id &&
                                !product.is_featured,
                            )
                            .slice(0, 6);

                          if (categoryProducts.length === 0) {
                            return null;
                          }

                          return (
                            <section key={`home-category-${category.id}`}>
                              <div className="mb-5 flex items-end justify-between gap-4">
                                <div className="min-w-0">
                                  <h2 className="text-2xl font-black text-slate-950">
                                    {category.name}
                                  </h2>

                                  {category.description && (
                                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                                      {category.description}
                                    </p>
                                  )}
                                </div>

                                {category.products_count >
                                  categoryProducts.length && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCatalogView("all");
                                      setCategoryId(category.id);
                                      setSubcategoryId(null);

                                      window.setTimeout(() => {
                                        document
                                          .getElementById("catalogo")
                                          ?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                          });
                                      }, 0);
                                    }}
                                    className="shrink-0 text-sm font-bold text-blue-600 transition hover:text-blue-700"
                                  >
                                    Ver todos
                                  </button>
                                )}
                              </div>

                              {company.business_type === "food" && (
                                <div className="sm:hidden grid grid-cols-1 gap-3">
                                  {categoryProducts.map((product) => (
                                    <FoodProductCard catalogOnly={!showCart}
                                      key={`food-home-category-${category.id}-${product.id}`}
                                      product={product}
                                      orderingDisabled={
                                        company.business_type === "food" &&
                                        company.food_availability?.is_open === false
                                      }
                                      quantity={cart
                                        .filter(
                                          (item) =>
                                            item.product.id === product.id,
                                        )
                                        .reduce(
                                          (sum, item) => sum + item.quantity,
                                          0,
                                        )}
                                      onOpen={() => {
                                        trackProductViewOncePerSession(
                                          company.slug,
                                          product.id,
                                        );

                                        openProductDetails(product);
                                      }}
                                      onAdd={() => addToCart(product)}
                                      onOpenCart={() => setIsCartOpen(true)}
                                    />
                                  ))}
                                </div>
                              )}

                              {/* CATEGORY_DESKTOP_SAME_AS_BEST_SELLERS */}
                              <div
                                className={`mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
                                  company.business_type === "food"
                                    ? "hidden sm:grid"
                                    : ""
                                }`}
                              >
                                {categoryProducts.map((product) => (
                                  <ProductCard catalogOnly={company.business_type === "food" && !showCart}
                                    key={`category-${product.id}`}
                                    product={product}
                                    orderingDisabled={
                                      company.business_type === "food" &&
                                      company.food_availability?.is_open === false
                                    }
                                    businessConfig={itemConfig(product)}
                                    location={
                                      company.business_type === "real_estate"
                                        ? [
                                            product.business_attributes
                                              ?.neighborhood,
                                            product.business_attributes?.city,
                                            company.state,
                                          ]
                                            .filter(Boolean)
                                            .join(" - ")
                                        : [company.city, company.state]
                                            .filter(Boolean)
                                            .join(" - ")
                                    }
                                    companySlug={company.slug}
                                    isFavorite={favoriteProductIds.includes(
                                      product.id,
                                    )}
                                    onToggleFavorite={() =>
                                      toggleFavoriteProduct(product.id)
                                    }
                                    quantity={cart
                                      .filter(
                                        (item) =>
                                          item.product.id === product.id,
                                      )
                                      .reduce(
                                        (sum, item) => sum + item.quantity,
                                        0,
                                      )}
                                    onOpen={() => {
                                      trackProductViewOncePerSession(
                                        company.slug,
                                        product.id,
                                      );

                                      openProductDetails(product);
                                    }}
                                    onAdd={() => addToCart(product)}
                                    onOpenCart={() => setIsCartOpen(true)}
                                    onFinancing={() => {
                                      if (
                                        company.business_type === "real_estate"
                                      ) {
                                        setRealEstateFinancingProductId(
                                          product.id,
                                        );
                                        setIsRealEstateFinancingOpen(true);
                                        return;
                                      }

                                      setVehicleLeadMode("financing");
                                    }}
                                  />
                                ))}
                              </div>
                            </section>
                          );
                        })}
                    </div>
                  </div>
                )}
              {!search.trim() &&
                categoryId === null &&
                subcategoryId === null &&
                (catalogView === "home" || catalogView === "all") &&
                !(
                  (company.remove_brand || company.business_type === "food") &&
                  catalogView === "home"
                ) && (
                  <StorefrontAdBanner
                    campaign={
                      campaignBanners?.between_most_viewed_and_categories
                    }
                    onProducts={openCampaignProducts}
                    onLead={
                      campaignLeadAllowed
                        ? () => {
                            const campaign =
                              campaignBanners?.between_most_viewed_and_categories;
                            if (campaign) setCampaignInterest(campaign);
                          }
                        : undefined
                    }
                    className="mt-10"
                  />
                )}
            </section>

    </div>

        </>
      )}

      {showCart && (cartItemsCount > 0 || retailRecovery) && (
        <button
          type="button"
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-5 left-1/2 z-30 flex w-[calc(100%-2.5rem)] max-w-xl -translate-x-1/2 items-center justify-between rounded-2xl bg-blue-600 px-5 py-4 text-white shadow-[0_20px_50px_rgba(37,99,235,0.35)] hover:bg-blue-700"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
              <ShoppingCart size={20} />
            </span>

            <div className="text-left">
              <p className="text-sm font-bold">
                {cartItemsCount || retailRecovery?.count} {(cartItemsCount || retailRecovery?.count) === 1 ? "item" : "itens"}
              </p>

              <p className="text-xs text-blue-100">{retailRecovery && !cartItemsCount ? retailRecovery.label : "Ver pedido"}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <strong>{formatCurrency(cartItemsCount ? cartTotal : retailRecovery?.total ?? 0)}</strong>

            <ChevronRight size={19} />
          </div>
        </button>
      )}

      {showCart && isCartOpen && (
        company.business_type === "retail" ? <RetailCart
          products={store.products} onRestore={setCart}
          company={{ ...company, primary_color: primaryColor || company.primary_color }}
          items={cart}
          total={cartTotal}
          giftPromotions={giftPromotions}
          quotePending={needsQuote && cart.length > 0 && cartQuoteState?.key !== cartQuoteKey}
          quoteError={needsQuote && cartQuoteState?.key === cartQuoteKey ? cartQuoteState.error : undefined}
          onRevalidate={revalidateCart}
          onClose={() => setIsCartOpen(false)}
          onUpdateQuantity={(item, quantity) => updateQuantity(getCartItemKeyFromItem(item), quantity)}
          onRemove={item => removeFromCart(getCartItemKeyFromItem(item))}
          onClear={clearCart}
        /> : <CartDrawer
          slug={company.slug}
          companyName={company.name}
          companyCity={company.city}
          businessType={company.business_type}
          foodAvailability={company.food_availability}
          localDeliveryEnabled={company.local_delivery_enabled}
          deliveryFeeMode={company.delivery_fee_mode}
          localDeliveryFee={company.local_delivery_fee}
          pickupEnabled={company.pickup_enabled}
          acceptPix={company.accept_pix}
          pixKey={company.pix_key}
          acceptCardOnDelivery={company.accept_card_on_delivery}
          acceptCashOnDelivery={company.accept_cash_on_delivery}
          deliveryRegions={company.delivery_regions}
          items={cart}
          total={cartTotal}
          giftPromotions={giftPromotions}
          quotePending={needsQuote && cart.length > 0 && cartQuoteState?.key !== cartQuoteKey}
          quoteError={needsQuote && cartQuoteState?.key === cartQuoteKey ? cartQuoteState.error : undefined}
          onRevalidate={revalidateCart}
          onClose={() => setIsCartOpen(false)}
          onUpdateQuantity={updateQuantity}
          onRemove={removeFromCart}
          onClear={clearCart}
        />
      )}

      <VerificationDetailsModal
        isOpen={isVerificationDetailsOpen}
        onClose={() => setIsVerificationDetailsOpen(false)}
        companyName={company.name}
        identityVerified={company.verification_identity_verified}
        registrationVerified={company.verification_registration_verified}
        addressVerified={company.verification_address_verified}
      />

      {selectedProductForModal && (
        <ProductDetailsModal
          catalogOnly={company.business_type === "food" && !showCart}
          hideWhatsapp={company.business_type === "retail"}
          product={selectedProductForModal}
          businessConfig={itemConfig(selectedProductForModal)}
          companySlug={company.slug}
          companyName={company.name}
          whatsapp={company.whatsapp}
          foodAvailabilityLabel={
            company.business_type === "food" &&
            company.food_availability?.is_open === false
              ? formatFoodAvailabilityLabel(company.food_availability)
              : undefined
          }
          isFavorite={favoriteProductIds.includes(selectedProductForModal.id)}
          onToggleFavorite={() => toggleFavoriteProduct(selectedProductForModal.id)}
          onClose={() => setSelectedProduct(null)}
          onAdd={(variation, configuration) => {
            if (!showCart) {
              return;
            }

            addCartItem(selectedProductForModal, variation, configuration);
            setSelectedProduct(null);
          }}
          onWhatsapp={() => trackWhatsappClick(company.slug, "product_details")}
        />
      )}

      {!isAutomotiveHome &&
        !isRealEstateHome &&
        !isRetailHome &&
        !isServicesHome && (
          <>
            <PublicCompanyReviews
              slug={company.slug}
              initialRatingAverage={ratingAverage}
              initialRatingCount={ratingCount}
            />

            {company.description && (
              <section
                id="sobre"
                className="scroll-mt-24 border-t border-slate-200 bg-slate-50"
              >
                <div className="mx-auto max-w-[1380px] px-5 py-8 lg:px-8 lg:py-12">
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:p-8">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                        <Building2 size={23} />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h2 className="text-xl font-bold text-slate-950">
                          Sobre a empresa
                        </h2>

                        <p
                          className={`mt-3 max-w-none whitespace-pre-line break-words text-sm leading-7 text-slate-600 sm:line-clamp-none ${
                            isAboutExpanded ? "" : "line-clamp-4"
                          }`}
                        >
                          {company.description}
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            setIsAboutExpanded((current) => !current)
                          }
                          className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-blue-600 transition hover:text-blue-700 sm:hidden"
                        >
                          {isAboutExpanded ? (
                            <>
                              Ver menos
                              <ChevronUp size={16} />
                            </>
                          ) : (
                            <>
                              Ver mais
                              <ChevronDown size={16} />
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                  </div>
                </div>
              </section>
            )}

            {true && (
              <>
                <StorefrontAdBanner
                  campaign={campaignBanners?.before_footer}
                  onProducts={openCampaignProducts}
                  onLead={
                    campaignLeadAllowed
                      ? () => {
                          const campaign = campaignBanners?.before_footer;
                          if (campaign) setCampaignInterest(campaign);
                        }
                      : undefined
                  }
                  className="mx-auto my-10 w-full max-w-[1380px] px-5 lg:px-8"
                />
                <footer className="mt-auto border-t border-slate-200 bg-white">
                  {(company.accept_pix ||
                    company.accept_card_on_delivery ||
                    company.accept_cash_on_delivery) && (
                    <div className="border-b border-slate-200">
                      <div className="mx-auto max-w-[1380px] px-5 py-7 lg:px-8">
                        <p className="text-center text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                          Formas de pagamento
                        </p>

                        <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
                          {company.accept_pix && (
                            <span
                              title="Pix"
                              aria-label="Pix"
                              className="inline-flex h-9 min-w-[52px] shrink-0 items-center justify-center"
                            >
                              <Image
                                src="/payments/pix.png"
                                alt="Pix"
                                width={954}
                                height={339}
                                sizes="58px"
                                className="max-h-7 w-auto max-w-[64px] object-contain"
                              />
                            </span>
                          )}

                          {company.accept_card_on_delivery &&
                            company.accepted_card_brands.map((brand) => (
                              <PaymentCardBrandMark key={brand} brand={brand} />
                            ))}

                          {company.accept_card_on_delivery &&
                            company.accepted_card_brands.length === 0 && (
                              <span className="inline-flex h-9 w-[58px] shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700">
                                <span
                                  className="relative block h-5 w-8"
                                  aria-hidden="true"
                                >
                                  <span className="absolute left-0 top-0 h-5 w-5 rounded-full bg-[#eb001b]" />
                                  <span className="absolute right-0 top-0 h-5 w-5 rounded-full bg-[#f79e1b]" />
                                </span>
                              </span>
                            )}

                          {company.accept_cash_on_delivery && (
                            <span
                              title="Dinheiro"
                              aria-label="Dinheiro"
                              className="inline-flex h-8 w-[46px] shrink-0 items-center justify-center rounded bg-emerald-600 text-white"
                            >
                              <FaMoneyBillWave size={22} aria-hidden="true" />
                            </span>
                          )}
                        </div>

                        {company.accept_card_on_delivery && (
                          <p className="mt-3 text-center text-xs text-slate-400">
                            Pagamento com cartão realizado na entrega ou
                            retirada.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {
                    <div className="mx-auto flex max-w-[1380px] flex-col items-center justify-between gap-2 px-5 py-6 text-center text-sm text-slate-500 sm:flex-row sm:text-left lg:px-8">
                      <div>
                        {/* Mobile */}
                        <div className="space-y-1.5 sm:hidden">
                          <p>
                            © {new Date().getFullYear()}{" "}
                            <span className="font-semibold text-slate-700">
                              {company.name}
                            </span>
                          </p>

                          <p className="text-xs text-slate-500">
                            {company.cnpj && (
                              <>
                                CNPJ {formatPublicCnpj(company.cnpj)}
                                {" • "}
                              </>
                            )}
                            Todos os direitos reservados.
                          </p>
                        </div>

                        {/* Desktop */}
                        <p className="hidden sm:block">
                          © {new Date().getFullYear()}{" "}
                          <span className="font-semibold text-slate-700">
                            {company.name}
                          </span>
                          {company.cnpj && (
                            <> • CNPJ {formatPublicCnpj(company.cnpj)}</>
                          )}
                          {" • Todos os direitos reservados."}
                        </p>
                      </div>

                      <p className="flex items-center justify-center gap-1.5 sm:justify-end">
                        <span>Desenvolvido por</span>
                        <span
                          aria-hidden="true"
                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm"
                        >
                          <Image
                            src="/images/logo-icon-fornecedor.png"
                            alt=""
                            width={713}
                            height={779}
                            sizes="20px"
                            className="h-4 w-4 object-contain"
                          />
                        </span>
                        <a
                          href="https://fornecedor.net"
                          target="_blank"
                          rel="noreferrer"
                          className="font-bold text-slate-700 transition hover:text-blue-600"
                        >
                          Fornecedor.Net
                        </a>
                      </p>
                    </div>
                  }
                </footer>
              </>
            )}
          </>
        )}
    </main>
  );
}


function getFoodCardPrice(product: PublicProduct) {
  const productPrice = Number(product.current_price ?? 0);

  if (productPrice > 0) {
    const normalPrice = Number(
      product.normal_price ?? product.current_price ?? 0,
    );

    return {
      kind: "price" as const,
      value: product.current_price,
      normalValue:
        normalPrice > productPrice
          ? String(product.normal_price)
          : null,
      hasPromotion:
        Boolean(product.active_promotion) &&
        normalPrice > productPrice,
      from: false,
    };
  }

  const availableVariations = (product.variations ?? [])
    .filter(
      (variation) =>
        variation.is_active &&
        variation.is_available &&
        Number(variation.current_price ?? 0) > 0,
    )
    .sort(
      (a, b) =>
        Number(a.current_price) -
        Number(b.current_price),
    );

  const cheapestVariation =
    availableVariations[0] ?? null;

  if (cheapestVariation) {
    const currentPrice = Number(
      cheapestVariation.current_price,
    );

    const normalPrice = Number(
      cheapestVariation.normal_price ??
        cheapestVariation.current_price,
    );

    return {
      kind: "price" as const,
      value: cheapestVariation.current_price,
      normalValue:
        Boolean(cheapestVariation.active_promotion) &&
        normalPrice > currentPrice
          ? String(cheapestVariation.normal_price)
          : null,
      hasPromotion:
        Boolean(cheapestVariation.active_promotion) &&
        normalPrice > currentPrice,
      from: true,
    };
  }

  const requiresConfiguration =
    product.has_variations ||
    (product.option_groups ?? []).some(
      (group) =>
        group.required ||
        group.configuration_type === "composition",
    );

  if (requiresConfiguration) {
    return {
      kind: "options" as const,
      value: null,
      normalValue: null,
      hasPromotion: false,
      from: false,
    };
  }

  return {
    kind: "price" as const,
    value: product.current_price,
    normalValue: null,
    hasPromotion: false,
    from: false,
  };
}

function FoodCompactProductCard({
  product,
  quantity,
  onOpen,
  onAdd,
  onOpenCart,
  orderingDisabled = false,
  catalogOnly = false,
}: {
  product: PublicProduct;
  quantity: number;
  onOpen: () => void;
  onAdd: () => void;
  onOpenCart: () => void;
  orderingDisabled?: boolean;
  catalogOnly?: boolean;
}) {
  const imageUrl =
    product.primary_image_url ??
    product.images?.[0]?.image_url ??
    null;

  const foodCardPrice = getFoodCardPrice(product);

  const hasPromotionalPrice =
    foodCardPrice.kind === "price" &&
    !foodCardPrice.from &&
    Number(product.current_price) > 0 &&
    Number(product.current_price) < Number(product.price);

  function handleAction(
    event: React.MouseEvent<HTMLButtonElement>,
  ) {
    event.stopPropagation();

    if (orderingDisabled || !product.is_available) {
      return;
    }

    if (
      product.has_variations ||
      product.variations.length > 0
    ) {
      onOpen();
      return;
    }

    if (quantity > 0) {
      onOpenCart();
      return;
    }

    onAdd();
  }

  return (
    <article
      onClick={onOpen}
      className="w-[42vw] min-w-[142px] max-w-[170px] shrink-0 cursor-pointer snap-start overflow-hidden rounded-2xl border border-slate-200 bg-white"
    >
      <div className="relative aspect-square w-full bg-slate-100">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package
              size={30}
              className="text-slate-300"
            />
          </div>
        )}

        {product.is_available && !catalogOnly && (
          <button
            type="button"
            aria-label={
              orderingDisabled
                ? "Loja fechada"
                : quantity > 0
                  ? "Abrir pedido"
                  : "Adicionar produto"
            }
            disabled={orderingDisabled}
            onClick={handleAction}
            className="absolute bottom-2 right-2 flex h-9 min-w-9 items-center justify-center rounded-full border-2 border-white bg-blue-600 px-2 text-white shadow-md transition active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:active:scale-100"
          >
            {quantity > 0 ? (
              <span className="text-xs font-black">
                {quantity}
              </span>
            ) : (
              <Plus size={19} strokeWidth={3} />
            )}
          </button>
        )}
      </div>

      <div className="flex min-h-[92px] flex-col p-3">
        <h3 className="line-clamp-2 text-[14px] font-extrabold leading-[18px] text-slate-900">
          {product.name}
        </h3>

        <div className="mt-auto pt-2">
          {foodCardPrice.kind === "options" ? (
            <span className="text-[13px] font-extrabold text-blue-600">
              Escolher opção
            </span>
          ) : (
            <div className="flex flex-wrap items-baseline gap-1.5">
              <span className="text-[14px] font-black text-slate-950">
                {foodCardPrice.from && (
                  <span className="mr-1 text-[10px] font-bold text-slate-500">
                    A partir de
                  </span>
                )}
                {formatCurrency(foodCardPrice.value)}
              </span>

              {hasPromotionalPrice && (
                <span className="text-[10px] font-semibold text-slate-400 line-through">
                  {formatCurrency(product.price)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function FoodProductCard({
  product,
  quantity,
  onOpen,
  onAdd,
  onOpenCart,
  orderingDisabled = false,
  catalogOnly = false,
}: {
  product: PublicProduct;
  quantity: number;
  onOpen: () => void;
  onAdd: () => void;
  onOpenCart: () => void;
  orderingDisabled?: boolean;
  catalogOnly?: boolean;
}) {
  const imageUrl =
    product.primary_image_url ?? product.images?.[0]?.image_url ?? null;

  const propertyAttributes = product.business_attributes ?? {};

  const preparationTime = propertyAttributes.preparation_time_minutes;

  const servesPeople = propertyAttributes.serves_people;

  const foodCardPrice = getFoodCardPrice(product);

  const hasPromotionalPrice =
    foodCardPrice.kind === "price" &&
    !foodCardPrice.from &&
    Number(product.current_price) > 0 &&
    Number(product.current_price) < Number(product.price);

  function handleAction(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();

    if (orderingDisabled || !product.is_available) {
      return;
    }

    if (product.has_variations || product.variations.length > 0) {
      onOpen();
      return;
    }

    if (quantity > 0) {
      onOpenCart();
      return;
    }

    onAdd();
  }

  return (
    <article
      onClick={onOpen}
      className="relative flex min-h-[138px] cursor-pointer gap-4 bg-white p-4 transition active:bg-slate-50"
    >
      <div className="min-w-0 flex-1 py-0.5">
        <h3 className="line-clamp-2 text-[15px] font-extrabold leading-5 text-slate-900">
          {product.name}
        </h3>

        {product.short_description && (
          <p className="mt-1.5 line-clamp-2 text-[13px] leading-5 text-slate-500">
            {product.short_description}
          </p>
        )}

        {(preparationTime !== undefined || servesPeople !== undefined) && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-400">
            {preparationTime !== undefined && (
              <span>{preparationTime} min</span>
            )}

            {servesPeople !== undefined && (
              <span>
                {servesPeople === 1
                  ? "Serve 1 pessoa"
                  : `Serve ${servesPeople} pessoas`}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-baseline gap-2">
          {foodCardPrice.kind === "options" ? (
            <span className="text-[14px] font-extrabold text-blue-600">
              Escolha as opções
            </span>
          ) : (
            <>
              <span className="text-[15px] font-black text-slate-950">
                {foodCardPrice.from && (
                  <span className="mr-1 text-xs font-bold text-slate-500">
                    A partir de
                  </span>
                )}
                {formatCurrency(foodCardPrice.value)}
              </span>

              {foodCardPrice.kind === "price" &&
                foodCardPrice.hasPromotion &&
                foodCardPrice.normalValue && (
                  <span className="text-xs font-semibold text-slate-400 line-through">
                    {formatCurrency(foodCardPrice.normalValue)}
                  </span>
                )}
            </>
          )}
        </div>
      </div>

      <div className="relative h-[110px] w-[110px] shrink-0 self-center">
        <div className="h-full w-full overflow-hidden rounded-xl bg-slate-100">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Package size={28} className="text-slate-300" />
            </div>
          )}
        </div>

        {product.is_available && !catalogOnly && (
          <button
            type="button"
            aria-label={
              orderingDisabled
                ? "Loja fechada"
                : quantity > 0
                  ? "Abrir pedido"
                  : "Adicionar produto"
            }
            disabled={orderingDisabled}
            onClick={handleAction}
            className="absolute -bottom-2 -right-2 flex h-9 min-w-9 items-center justify-center rounded-full border-2 border-white bg-blue-600 px-2 text-white shadow-md transition active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:active:scale-100"
          >
            {quantity > 0 ? (
              <span className="text-xs font-black">{quantity}</span>
            ) : (
              <Plus size={19} strokeWidth={3} />
            )}
          </button>
        )}
      </div>
    </article>
  );
}

function ProductCard({
  product,
  businessConfig,
  location,
  companySlug,
  isFavorite,
  onToggleFavorite,
  quantity,
  onOpen,
  onAdd,
  onOpenCart,
  onFinancing,
  orderingDisabled = false,
  catalogOnly = false,
}: {
  product: PublicProduct;
  businessConfig: BusinessConfig;
  location: string;
  companySlug: string;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  quantity: number;
  onOpen: () => void;
  onAdd: () => void;
  onOpenCart: () => void;
  onFinancing: () => void;
  orderingDisabled?: boolean;
  catalogOnly?: boolean;
}) {
  const showCart =
    !catalogOnly && businessConfig.commerceMode === "order" && product.item_kind === "product";
  const foodCardPrice =
    businessConfig.businessType === "food"
      ? getFoodCardPrice(product)
      : null;

  const hasDynamicProductPromotion =
    Boolean(product.active_promotion) &&
    Number(product.current_price ?? 0) > 0 &&
    Number(product.normal_price ?? 0) >
      Number(product.current_price ?? 0);

  const hasStaticProductPromotion =
    Boolean(product.promotional_price) &&
    Number(product.promotional_price ?? 0) > 0 &&
    Number(product.promotional_price ?? 0) <
      Number(product.price ?? 0);

  const hasDisplayedPromotion =
    businessConfig.businessType === "food"
      ? Boolean(
          foodCardPrice?.kind === "price" &&
          foodCardPrice.hasPromotion,
        )
      : hasDynamicProductPromotion ||
        hasStaticProductPromotion;

  const displayedNormalPrice =
    businessConfig.businessType === "food" &&
    foodCardPrice?.kind === "price" &&
    foodCardPrice.normalValue
      ? foodCardPrice.normalValue
      : hasDynamicProductPromotion
        ? product.normal_price
        : product.price;
  const imageUrls = Array.from(
    new Set(
      [
        product.primary_image_url,
        ...(product.images ?? []).map((image) => image.image_url),
      ].filter((url): url is string => Boolean(url)),
    ),
  );

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const didSwipe = useRef(false);

  const currentImageUrl = imageUrls[currentImageIndex] ?? imageUrls[0] ?? null;

  const productSizes = product.has_variations
    ? Array.from(
        new Set(
          product.variations
            .filter((variation) => variation.is_active)
            .flatMap((variation) =>
              variation.values
                .filter(
                  (value) =>
                    value.attribute_name.trim().toLocaleLowerCase("pt-BR") ===
                    "tamanho",
                )
                .map((value) => value.value.trim())
                .filter(Boolean),
            ),
        ),
      )
    : product.size.trim()
      ? [product.size.trim()]
      : [];

  const propertyAttributes = product.business_attributes ?? {};

  const foodTypeLabel = propertyAttributes.food_type
    ? getFoodTypeLabel(propertyAttributes.food_type)
    : null;

  const foodHighlights = [
    foodTypeLabel,
    propertyAttributes.preparation_time_minutes !== undefined
      ? `${propertyAttributes.preparation_time_minutes} min`
      : null,
    propertyAttributes.serves_people !== undefined
      ? propertyAttributes.serves_people === 1
        ? "Serve 1 pessoa"
        : `Serve ${propertyAttributes.serves_people} pessoas`
      : null,
  ].filter((value): value is string => Boolean(value));

  const propertyHighlights = [
    propertyAttributes.area_m2 ? `${propertyAttributes.area_m2} m²` : null,
    propertyAttributes.bedrooms
      ? `${propertyAttributes.bedrooms} ${propertyAttributes.bedrooms === 1 ? "dormitório" : "dormitórios"}`
      : null,
    propertyAttributes.parking_spaces
      ? `${propertyAttributes.parking_spaces} ${propertyAttributes.parking_spaces === 1 ? "vaga" : "vagas"}`
      : null,
  ].filter((value): value is string => Boolean(value));

  const vehicleTransmissionLabel = propertyAttributes.transmission
    ? {
        manual: "Manual",
        automatic: "Automático",
        cvt: "CVT",
        automated: "Automatizado",
        other: "Outro",
      }[propertyAttributes.transmission]
    : null;

  const vehicleYear =
    propertyAttributes.manufacture_year !== undefined ||
    propertyAttributes.model_year !== undefined
      ? [propertyAttributes.manufacture_year, propertyAttributes.model_year]
          .filter((value) => value !== undefined && value !== null)
          .join("/")
      : null;

  const vehicleHighlights = [
    vehicleYear,
    propertyAttributes.mileage !== undefined
      ? `${propertyAttributes.mileage.toLocaleString("pt-BR")} km`
      : null,
    vehicleTransmissionLabel,
  ].filter((value): value is string => Boolean(value));

  const propertyLocation = [
    propertyAttributes.neighborhood,
    propertyAttributes.city,
  ]
    .filter(Boolean)
    .join(" — ");

  const visibleProductSizes = productSizes.slice(0, 5);

  const hiddenProductSizesCount = Math.max(
    productSizes.length - visibleProductSizes.length,
    0,
  );

  function showPreviousImage() {
    setCurrentImageIndex((current) =>
      current === 0 ? imageUrls.length - 1 : current - 1,
    );
  }

  function showNextImage() {
    setCurrentImageIndex((current) =>
      current === imageUrls.length - 1 ? 0 : current + 1,
    );
  }

  function handleTouchStart(event: React.TouchEvent<HTMLDivElement>) {
    if (imageUrls.length <= 1) {
      return;
    }

    const touch = event.touches[0];

    didSwipe.current = false;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  }

  function handleTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    if (
      imageUrls.length <= 1 ||
      touchStartX.current === null ||
      touchStartY.current === null
    ) {
      return;
    }

    const touch = event.changedTouches[0];

    const deltaX = touch.clientX - touchStartX.current;

    const deltaY = touch.clientY - touchStartY.current;

    touchStartX.current = null;
    touchStartY.current = null;

    const minimumSwipeDistance = 50;

    if (
      Math.abs(deltaX) < minimumSwipeDistance ||
      Math.abs(deltaX) <= Math.abs(deltaY)
    ) {
      return;
    }

    didSwipe.current = true;

    if (deltaX < 0) {
      showNextImage();
      return;
    }

    showPreviousImage();
  }

  async function handleShare(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();

    const hostname = window.location.hostname;

    const isPlatformRoot =
      hostname === "fornecedor.net" ||
      hostname === "www.fornecedor.net" ||
      hostname === "dev.fornecedor.net" ||
      hostname === "fornecedor.dev" ||
      hostname === "www.fornecedor.dev";

    const storefrontUrl = isPlatformRoot
      ? `${window.location.origin}/${encodeURIComponent(companySlug)}`
      : window.location.origin;

    const productUrl = `${storefrontUrl}${getPublicItemPath(
      businessConfig,
      product,
    )}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          url: productUrl,
        });

        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      }
    }

    const whatsappText = encodeURIComponent(`${product.name}\n\n${productUrl}`);

    window.open(
      `https://wa.me/?text=${whatsappText}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  return (
    <article
      onClick={() => {
        if (didSwipe.current) {
          didSwipe.current = false;
          return;
        }

        onOpen();
      }}
      className="group grid min-w-0 w-full max-w-full cursor-pointer grid-cols-[128px_minmax(0,1fr)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition sm:block sm:rounded-3xl sm:hover:-translate-y-1 sm:hover:shadow-xl"
    >
      <div
        className="relative h-full min-h-[128px] w-[128px] touch-pan-y overflow-visible bg-slate-100 sm:aspect-[4/3] sm:h-auto sm:min-h-0 sm:w-full sm:overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {currentImageUrl ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url("${currentImageUrl}")`,
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300">
            <Package size={45} />
          </div>
        )}

        {product.availability_status !== "available" ? (
          <span className="absolute left-4 top-4 z-10 inline-flex rounded-full bg-rose-600 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-white shadow">
            {product.availability_status === "sold"
              ? "Vendido"
              : businessConfig.businessType === "retail" ||
                  businessConfig.businessType === "wholesale"
                ? "Esgotado"
                : "Indisponível"}
          </span>
        ) : product.is_featured ? (
          <span
            title={
              product.item_kind === "service"
                ? "Serviço em destaque"
                : "Produto em destaque"
            }
            aria-label={
              product.item_kind === "service"
                ? "Serviço em destaque"
                : "Produto em destaque"
            }
            className="absolute left-2 top-2 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full border border-amber-200 bg-white/95 text-amber-500 shadow-sm backdrop-blur-sm sm:left-4 sm:top-4 sm:h-8 sm:w-8"
          >
            <Star
              size={13}
              strokeWidth={2.5}
              className="fill-current sm:h-[17px] sm:w-[17px]"
            />
          </span>
        ) : null}

        {imageUrls.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Imagem anterior"
              onClick={(event) => {
                event.stopPropagation();
                showPreviousImage();
              }}
              className="absolute left-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] transition hover:scale-110 sm:flex"
            >
              <ChevronLeft size={21} />
            </button>

            <button
              type="button"
              aria-label="Próxima imagem"
              onClick={(event) => {
                event.stopPropagation();
                showNextImage();
              }}
              className="absolute right-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] transition hover:scale-110 sm:flex"
            >
              <ChevronRight size={21} />
            </button>
          </>
        )}

        {showCart && product.is_available && (
          <button
            type="button"
            aria-label={quantity > 0 ? "Abrir pedido" : "Adicionar produto"}
            onClick={(event) => {
              event.stopPropagation();

              if (quantity > 0 && !product.has_variations) {
                onOpenCart();
                return;
              }

              onAdd();
            }}
            className="absolute bottom-0 left-1 z-20 flex h-9 min-w-9 items-center justify-center rounded-full border-2 border-white bg-blue-600 px-2 text-white shadow-md transition active:scale-95 sm:hidden"
          >
            {quantity > 0 && !product.has_variations ? (
              <span className="text-xs font-black">{quantity}</span>
            ) : (
              <Plus size={19} strokeWidth={3} />
            )}
          </button>
        )}
      </div>

      <div className="min-w-0 max-w-full overflow-hidden px-2.5 py-2 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 break-words text-xs font-bold uppercase tracking-wide text-blue-600">
            {product.category_name || businessConfig.itemSingular}
          </p>

          <div className="-mr-1 -mt-1 flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label={
                isFavorite
                  ? `Remover ${product.name} dos favoritos`
                  : `Adicionar ${product.name} aos favoritos`
              }
              title={
                isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"
              }
              aria-pressed={isFavorite}
              onClick={(event) => {
                event.stopPropagation();
                onToggleFavorite();
              }}
              className={`p-1 transition hover:scale-110 ${
                isFavorite
                  ? "text-rose-500"
                  : "text-slate-500 hover:text-rose-500"
              }`}
            >
              <Heart
                size={19}
                className={
                  isFavorite ? "fill-rose-500 text-rose-500" : "fill-none"
                }
              />
            </button>

            <button
              type="button"
              aria-label={`Compartilhar ${product.name}`}
              title="Compartilhar"
              onClick={handleShare}
              className="p-1 text-slate-500 transition hover:text-blue-600"
            >
              <Share2 size={19} />
            </button>
          </div>
        </div>

        <h3 className="mt-0.5 line-clamp-1 break-words text-[13px] font-bold leading-4 text-slate-950 sm:mt-2 sm:line-clamp-2 sm:text-lg sm:leading-normal">
          {product.name}
        </h3>

        {location && (
          <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[10px] font-medium leading-4 text-slate-500 sm:hidden">
            <MapPin size={12} className="shrink-0 text-blue-600" />
            <span className="min-w-0 flex-1 truncate">{location}</span>
          </div>
        )}

        {(businessConfig.businessType === "food"
          ? propertyAttributes.menu_code
          : product.reference_code) && (
          <p className="mt-1 hidden text-[11px] font-bold uppercase tracking-wide text-slate-400 sm:block">
            {businessConfig.businessType === "food"
              ? `Ref. ${propertyAttributes.menu_code}`
              : `Ref. ${product.reference_code}`}
          </p>
        )}

        <p className="hidden sm:mt-2 sm:line-clamp-2 sm:min-h-10 sm:break-words sm:text-sm sm:leading-5 sm:text-slate-500">
          {product.short_description ||
            "Consulte a empresa para mais informações."}
        </p>

        {businessConfig.businessType === "food" ? (
          foodHighlights.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-bold text-slate-600">
              {foodHighlights.map((highlight, index) => (
                <span key={highlight}>
                  {index > 0 && <span className="mr-2 text-slate-300">•</span>}

                  {highlight}
                </span>
              ))}
            </div>
          )
        ) : businessConfig.businessType === "real_estate" ? (
          <>
            {propertyHighlights.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] font-bold leading-4 text-slate-700 sm:mt-3 sm:gap-x-2 sm:gap-y-1 sm:text-sm sm:leading-normal">
                {propertyHighlights.map((highlight, index) => (
                  <span key={highlight}>
                    {index > 0 && (
                      <span className="mr-2 text-slate-300">•</span>
                    )}
                    {highlight}
                  </span>
                ))}
              </div>
            )}

            {propertyLocation && (
              <div className="mt-1 hidden items-center gap-1 text-[10px] font-medium leading-4 text-slate-500 sm:mt-3 sm:flex sm:gap-1.5 sm:text-sm sm:leading-normal">
                <MapPin size={15} className="shrink-0 text-blue-600" />
                <span className="min-w-0 flex-1 truncate">
                  {propertyLocation}
                </span>
              </div>
            )}
          </>
        ) : businessConfig.businessType === "vehicles" ? (
          vehicleHighlights.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] font-bold leading-4 text-slate-700 sm:mt-3 sm:gap-x-2 sm:gap-y-1 sm:text-sm sm:leading-normal">
              {vehicleHighlights.map((highlight, index) => (
                <span key={highlight}>
                  {index > 0 && <span className="mr-2 text-slate-300">•</span>}

                  {highlight}
                </span>
              ))}
            </div>
          )
        ) : (
          visibleProductSizes.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {visibleProductSizes.map((size) => (
                <span
                  key={size}
                  className="inline-flex min-w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-bold text-slate-700"
                >
                  {size}
                </span>
              ))}

              {hiddenProductSizesCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-500">
                  +{hiddenProductSizesCount}
                </span>
              )}
            </div>
          )
        )}

        <div className="mt-1 sm:mt-5">
          {hasDisplayedPromotion ? (
            <>
              <div className="flex items-end justify-between gap-2 sm:hidden">
                <p className="min-w-0 text-xs font-medium text-slate-400 line-through">
                  {formatCurrency(displayedNormalPrice)}
                </p>

                <p className="min-w-0 max-w-[65%] break-words text-right text-[15px] font-black leading-5 text-slate-950">
                  {businessConfig.businessType === "food" && foodCardPrice
                ? foodCardPrice.kind === "options"
                  ? "Escolha as opções"
                  : `${foodCardPrice.from ? "A partir de " : ""}${formatCurrency(foodCardPrice.value)}`
                : formatCurrency(product.current_price)}
                </p>
              </div>

              <div className="hidden sm:block">
                <p className="text-xs font-medium text-slate-400 line-through">
                  {formatCurrency(displayedNormalPrice)}
                </p>

                <p className="max-w-full break-words text-xl font-black text-slate-950">
                  {businessConfig.businessType === "food" && foodCardPrice
                ? foodCardPrice.kind === "options"
                  ? "Escolha as opções"
                  : `${foodCardPrice.from ? "A partir de " : ""}${formatCurrency(foodCardPrice.value)}`
                : formatCurrency(product.current_price)}
                </p>
              </div>
            </>
          ) : (
            <p className="max-w-full break-words text-[15px] font-black leading-5 text-slate-950 sm:text-xl sm:leading-normal">
              {businessConfig.businessType === "food" && foodCardPrice
                ? foodCardPrice.kind === "options"
                  ? "Escolha as opções"
                  : `${foodCardPrice.from ? "A partir de " : ""}${formatCurrency(foodCardPrice.value)}`
                : formatCurrency(product.current_price)}
            </p>
          )}
        </div>
      </div>

      <div
        className={
          showCart
            ? "hidden sm:block sm:border-0 sm:bg-transparent sm:px-5 sm:pb-5 sm:pt-0"
            : "col-span-2 border-t border-slate-100 bg-slate-50 px-2 py-2 sm:border-0 sm:bg-transparent sm:px-5 sm:pb-5 sm:pt-0"
        }
      >
        {showCart && (
          <div className="mb-1 hidden items-center gap-2 text-[10px] font-semibold sm:mb-0 sm:mt-3 sm:flex sm:text-xs">
            {product.is_available ? (
              <>
                <CheckCircle2 size={15} className="text-emerald-500" />

                <span className="text-emerald-700">Disponível</span>
              </>
            ) : (
              <span className="font-bold text-rose-600">
                {product.availability_status === "sold"
                  ? "Vendido"
                  : businessConfig.businessType === "retail" ||
                      businessConfig.businessType === "wholesale"
                    ? "Esgotado"
                    : "Indisponível"}
              </span>
            )}
          </div>
        )}

        {businessConfig.commerceMode === "lead" ? (
          businessConfig.businessType === "vehicles" ||
          businessConfig.businessType === "real_estate" ? (
            <>
              <div className="grid grid-cols-2 gap-2 sm:hidden">
                <button
                  type="button"
                  disabled={!product.is_available || orderingDisabled}
                  onClick={(event) => {
                    event.stopPropagation();

                    if (!product.is_available) {
                      return;
                    }

                    onFinancing();
                  }}
                  className="inline-flex h-10 items-center justify-center rounded-xl border border-blue-600 bg-white px-2 text-xs font-bold text-blue-600 disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400"
                >
                  Financiar
                </button>

                <button
                  type="button"
                  disabled={!product.is_available || orderingDisabled}
                  onClick={(event) => {
                    event.stopPropagation();

                    if (!product.is_available) {
                      return;
                    }

                    onOpen();
                  }}
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-blue-600 px-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  <MessageCircle size={15} />
                  {product.is_available
                    ? "Tenho interesse"
                    : product.availability_status === "sold"
                      ? "Vendido"
                      : "Indisponível"}
                </button>
              </div>

              <button
                type="button"
                disabled={!product.is_available || orderingDisabled}
                onClick={(event) => {
                  event.stopPropagation();

                  if (!product.is_available) {
                    return;
                  }

                  onOpen();
                }}
                className="mt-5 hidden h-11 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:inline-flex"
              >
                <MessageCircle size={18} />
                {product.is_available
                  ? businessConfig.primaryCta
                  : product.availability_status === "sold"
                    ? "Vendido"
                    : "Indisponível"}
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={!product.is_available || orderingDisabled}
              onClick={(event) => {
                event.stopPropagation();

                if (!product.is_available) {
                  return;
                }

                onOpen();
              }}
              className="mt-0 inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:mt-5 sm:h-11"
            >
              <MessageCircle size={18} />
              {product.is_available
                ? businessConfig.primaryCta
                : product.availability_status === "sold"
                  ? "Vendido"
                  : "Indisponível"}
            </button>
          )
        ) : quantity > 0 && !product.has_variations ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenCart();
            }}
            className="mt-5 hidden h-11 w-full items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 text-sm font-bold text-blue-700 hover:bg-blue-100 sm:inline-flex"
          >
            <ShoppingCart size={18} />
            {quantity} no pedido
          </button>
        ) : (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onAdd();
            }}
            disabled={!product.is_available || orderingDisabled}
            className="mt-5 hidden h-11 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:inline-flex"
          >
            <Plus size={18} />

            {orderingDisabled
              ? "Loja fechada"
              : product.has_variations && quantity > 0
                ? "Adicionar outra variação"
                : product.has_variations
                  ? "Escolher opções"
                  : businessConfig.primaryCta}
          </button>
        )}
      </div>
    </article>
  );
}
